import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, fileUrl, tokenStore, API as API_BASE } from "../lib/api";
import { DropZone } from "../components/DropZone";
import { useAuth } from "../context/AuthContext";
import { useSyncWatch } from "../context/SyncContext";
import { useNavigate } from "react-router-dom";
import MissingReportsBanner from "../components/MissingReportsBanner";
import DriverMaintenanceBanner from "../components/DriverMaintenanceBanner";
import BagsAfterHours from "../components/BagsAfterHours";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, Clock, Fuel, Trash2, AlertTriangle, Plane, BarChart3, Package2, Wrench, Users as UsersIcon, Video, FlagOff, Hammer, Check, Package, ArrowUpRight, ArrowRight, Gauge, ListTodo, X, Undo2, ImageOff, FileSignature, Upload, FileText, Download, Trash, Car } from "lucide-react";
import { UploadButton, AuthImg } from "../components/AuthImg";
import { CompleteTaskDialog } from "./Tasks";
import { TaskAttachmentList } from "../components/TaskAttachments";
import { EquipmentTransferCards } from "../components/EquipmentTransfer";
import AssignedTasksBanner from "../components/AssignedTasksBanner";
import RepairOrServiceDialog from "./cars/RepairOrServiceDialog";
import { fmtRoDate, fmtRoDateTime } from "../lib/dateFmt";
import VideoExplicativeCard from "../components/VideoExplicativeCard";
import GuidedTour from "../components/GuidedTour";
import ErrorBoundary from "../components/ErrorBoundary";
import SimpleHoursCard from "../components/SimpleHoursCard";

const ACTIONS = [
  { key: "plan",       label: "Vizualizează Plan",  icon: CalendarDays, color: "#3B82F6" },
  { key: "hours",      label: "Adaugă Ore",          icon: Clock,        color: "#10B981" },
  { key: "fuel",       label: "Alimentează",         icon: Fuel,         color: "#F59E0B" },
  { key: "car_maint",  label: "Întreținere mașini",  icon: Wrench,       color: "#0D9488", navigate: "/intretinere-masini" },
  { key: "bags",       label: "Aruncă Saci",         icon: Trash2,       color: "#7C3AED" },
  { key: "defect",     label: "Raportează Defect",   icon: AlertTriangle, color: "#EF4444" },
  { key: "cars_view",  label: "Autoturisme",         icon: Car,          color: "#334155" },
  { key: "vacation",   label: "Cerere Concediu",     icon: Plane,        color: "#06B6D4" },
  { key: "stats",      label: "Orele mele lună",     icon: BarChart3,    color: "#EC4899" },
  { key: "reports",    label: "Rapoarte semnate",    icon: FileSignature, color: "#475569" },
  { key: "inventory",  label: "Ia din Inventar",     icon: Package2,     color: "#0EA5E9" },
  { key: "my_equipment", label: "Echipament alocat", icon: Package,      color: "#1D4ED8" },
  { key: "return_inventory", label: "Returnează în Inventar", icon: Undo2, color: "#0891B2" },
  { key: "start_zone", label: "Start zonă (SUVA)",   icon: Video,        color: "#16A34A" },
  { key: "finalize",   label: "Finalizează șantier", icon: FlagOff,      color: "#9333EA" },
  { key: "tasks_nav",  label: "Sarcini",             icon: ListTodo,     color: "#16A34A", navigate: "/tasks" },
];

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);
  const isMagazioner = user?.role === "magazioner";

  // Lista pașilor turului — selectori DOM pentru fiecare element evidențiat
  const TOUR_STEPS = useMemo(() => ([
    {
      title: "Bun venit la ConVic! 👋",
      body: `Salut, ${user?.name || "coleg"}! Îți facem un tur scurt al platformei (durează ~1 minut).\n\nVei vedea unde adaugi orele, ce echipamente ai, planul săptămânii, și multe altele.\n\nUsesti săgețile ← / → pentru a naviga, sau butoanele de mai jos.`,
      icon: "✨",
    },
    {
      selector: '[data-testid="employee-dash-logo"]',
      title: "Acesta e tabloul tău de bord",
      body: "Aici găsești tot ce ai nevoie ca muncitor: planul, orele, echipamentul, concediul și mai mult.",
      icon: "🏠",
      placement: "right",
    },
    {
      selector: '[data-testid="emp-btn-plan"]',
      title: "Vezi unde lucrezi azi sau mâine",
      body: "Apasă aici să vezi planificarea ta pentru ziua curentă sau pentru săptămâna întreagă (cu colegii și mașina alocată).",
      icon: "📅",
      placement: "bottom",
    },
    {
      selector: '[data-testid="emp-btn-hours"]',
      title: "Înregistrează orele tale și ale echipei",
      body: "La sfârșitul zilei apasă aici. Vei vedea automat colegii cu care ai fost planificat și șoferii evidențiați. Poți adăuga și pe cineva care a venit neplanificat — din lista de colegi.",
      icon: "⏰",
      placement: "bottom",
    },
    {
      selector: '[data-testid="emp-btn-inventory"]',
      title: "Ia echipamente din magazie",
      body: "Când ai nevoie de o sculă sau de PSA, intri aici, găsești articolul (poți căuta și după serie), apoi confirmă. Echipamentul rămâne în responsabilitatea ta.",
      icon: "📦",
      placement: "top",
    },
    {
      selector: '[data-testid="emp-btn-my_equipment"]',
      title: "Echipamentul tău",
      body: "Aici vezi exact ce ai în responsabilitate momentan. Util când ștabul te întreabă dacă mai ai sigur cheia franceză.",
      icon: "🧰",
      placement: "top",
    },
    {
      selector: '[data-testid="emp-btn-stats"]',
      title: "Statistici personale lunare",
      body: "Vezi câte ore ai lucrat, câte ai condus și în ce zile. Te ajută să verifici la sfârșitul lunii dacă totul e ok.",
      icon: "📊",
      placement: "top",
    },
    {
      selector: '[data-testid="emp-btn-vacation"]',
      title: "Cerere de concediu",
      body: "Vrei zile libere? Apeși aici, alegi perioada și motivul. Cererea ajunge la administrator pentru aprobare.",
      icon: "🏖️",
      placement: "top",
    },
    {
      selector: '[data-testid="emp-btn-tasks_nav"]',
      title: "Sarcinile tale",
      body: "Aici găsești task-urile care ți-au fost atribuite (ex: adu sculă X la șantierul Y mâine). Le marchezi ca executate când le-ai terminat.",
      icon: "✅",
      placement: "top",
    },
    {
      selector: '[data-testid="employee-change-password-btn"]',
      title: "Schimbă-ți parola",
      body: "Dacă vrei o parolă mai simplă (sau mai sigură!), o poți schimba oricând de aici.",
      icon: "🔑",
      placement: "left",
    },
    {
      selector: '[data-testid="employee-restart-tour-btn"]',
      title: "Reluare tur oricând 🔁",
      body: "Gata! Dacă vrei să revezi turul, apeși acest buton. Sper că ți-e mai ușor acum. Spor la treabă!",
      icon: "🎉",
      placement: "left",
    },
  ]), [user?.name]);

  // Auto-pornire la primul login pentru muncitori
  useEffect(() => {
    if (!user) return;
    // Doar pentru muncitori (user, magazioner, planificator) — adminul nu primește turul
    if (user.role === "admin") return;
    let cancelled = false;
    api.get("/me/tour-status").then((r) => {
      if (cancelled) return;
      if (!r.data?.tour_completed) {
        // Mic delay ca paginia să fie randată complet
        setTimeout(() => { if (!cancelled) setTourOpen(true); }, 800);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const completeTour = useCallback(() => {
    api.post("/me/tour-complete").catch(() => {});
  }, []);

  const restartTour = () => {
    api.post("/me/tour-reset").catch(() => {});
    setTourOpen(true);
  };

  const allActions = useMemo(() => {
    let list = ACTIONS;
    if (isMagazioner) {
      // Insert "Repară" before the trailing "Sarcini" so Sarcini stays last.
      const head = ACTIONS.slice(0, -1);
      const tail = ACTIONS.slice(-1);
      list = [...head, { key: "repair", label: "Repară", icon: Wrench, color: "#F97316" }, ...tail];
    }
    if (user?.simple_hours_calendar) {
      list = [{ key: "ore_simple", label: "Calendar Ore", icon: CalendarDays, color: "#059669", navigate: "/ore-simple" }, ...list];
    }
    return list;
  }, [isMagazioner, user?.simple_hours_calendar]);

  return (
    <div className="swiss-black-dash space-y-8" data-testid="employee-dashboard">
      <AssignedTasksBanner />
      <SimpleHoursCard />
      <DriverMaintenanceBanner />
      {/* Hero */}
      <MissingReportsBanner />
      <header className="flex items-start justify-between gap-6 pt-2">
        <div className="flex items-start gap-5">
          {/* Round company logo */}
          <a
            href="https://www.convicgmbh.ch"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:block group/logo flex-shrink-0"
            data-testid="employee-dash-logo"
            title="ConVic GmbH"
          >
            <div className="relative">
              <div className="absolute -inset-1 rounded-full border border-red-600/30 group-hover/logo:border-red-600/70 transition-colors duration-500" aria-hidden="true" />
              <div className="relative w-20 h-20 rounded-full overflow-hidden ring-1 ring-white/15 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.4)] transition-transform duration-500 group-hover/logo:scale-[1.04]">
                <img
                  src="/logo-convic.jpg"
                  alt="ConVic"
                  className="w-full h-full object-cover scale-[1.02] select-none"
                  draggable="false"
                />
              </div>
            </div>
          </a>
          <div>
            <div className="swiss-marker mb-3">// Bord angajat</div>
            <h1 className="hero-h1 font-display text-4xl sm:text-5xl lg:text-6xl">
              Bună,<br/>
              <span className="text-red-600">{user?.name || user?.username}</span>.
            </h1>
            <p className="text-sm muted mt-3 tracking-wide">Ce vrei să faci azi?</p>
          </div>
        </div>
        <div className="hidden sm:flex flex-col items-end gap-1">
          <div className="swiss-marker">№ {new Date().toISOString().slice(0,10).replace(/-/g,'.')}</div>
          <svg width="28" height="28" viewBox="0 0 36 36" aria-hidden="true">
            <rect width="36" height="36" fill="#DC2626" />
            <rect x="15" y="6" width="6" height="24" fill="#fff" />
            <rect x="6" y="15" width="24" height="6" fill="#fff" />
          </svg>
          <button
            type="button"
            onClick={() => setActive("change_password")}
            className="mt-2 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded border border-white/20 text-white/80 hover:bg-white/10 hover:text-white transition-colors flex items-center gap-1.5"
            data-testid="employee-change-password-btn"
            title="Schimbă-ți parola de logare"
          >
            🔑 Schimbă parola
          </button>
          {user?.role !== "admin" && (
            <button
              type="button"
              onClick={restartTour}
              className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 hover:text-amber-200 transition-colors flex items-center gap-1.5"
              data-testid="employee-restart-tour-btn"
              title="Pornește din nou turul ghidat"
            >
              🎓 Tur
            </button>
          )}
        </div>
      </header>

      <ReportReminderBanner />

      <TodayCarBanner />

      <TomorrowBanner onOpenPlan={() => setActive("plan")} />

      <TasksBanner onOpen={() => setActive("tasks")} />

      {isMagazioner && <EquipmentTransferCards />}

      {isMagazioner && <MissingRevizieBanner navigate={navigate} />}

      {isMagazioner && <ServiceDueBanner navigate={navigate} />}

      <DismantleAlertBanner onOpen={() => setActive("dismantle")} />

      {/* Action tiles — Swiss grid */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-8 bg-red-600"/>
          <span className="swiss-marker">Acțiuni rapide</span>
          <div className="flex-1 h-px bg-white/10"/>
          <span className="swiss-marker text-white/30">{allActions.length.toString().padStart(2,'0')}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-0 border border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
          {allActions.map((a, idx) => {
            const Icon = a.icon;
            return (
              <button
                key={a.key}
                data-testid={`emp-btn-${a.key}`}
                onClick={() => { if (a.navigate) navigate(a.navigate); else setActive(a.key); }}
                className="sb-tile"
                style={{ "--tile-accent": a.color }}
              >
                <div className="flex items-center justify-between">
                  <Icon size={24} className="sb-icon"/>
                  <span className="sb-num">{String(idx + 1).padStart(2, "0")}</span>
                </div>
                <div className="sb-label">{a.label}</div>
                <ArrowUpRight size={16} className="sb-arrow"/>
              </button>
            );
          })}
        </div>
      </div>

      {active === "plan" && <PlanModal onClose={() => setActive(null)}/>}
      {active === "tasks" && <TasksModal onClose={() => setActive(null)}/>}
      {active === "hours" && (
        <ErrorBoundary>
          <HoursModal onClose={() => setActive(null)}/>
        </ErrorBoundary>
      )}
      {active === "fuel" && <FuelModal onClose={() => setActive(null)}/>}
      {active === "vacation" && <VacationModal onClose={() => setActive(null)}/>}
      {active === "stats" && <StatsModal onClose={() => setActive(null)}/>}
      {active === "cars_view" && <CarsViewModal onClose={() => setActive(null)}/>}
      {active === "reports" && <ReportsModal onClose={() => setActive(null)}/>}
      {active === "bags" && <BagsModal onClose={() => setActive(null)}/>}
      {active === "defect" && <DefectModal onClose={() => setActive(null)}/>}
      {active === "inventory" && <InventoryTakeModal onClose={() => setActive(null)}/>}
      {active === "my_equipment" && <MyEquipmentModal onClose={() => setActive(null)}/>}
      {active === "return_inventory" && <ReturnInventoryModal onClose={() => setActive(null)}/>}
      {active === "repair" && <RepairModal onClose={() => setActive(null)}/>}
      {active === "start_zone" && <StartZoneModal onClose={() => setActive(null)}/>}
      {active === "finalize" && <FinalizeSiteModal onClose={() => setActive(null)}/>}
      {active === "dismantle" && <DismantleListModal onClose={() => setActive(null)}/>}
      {active === "change_password" && <ChangePasswordModal onClose={() => setActive(null)}/>}

      <GuidedTour
        open={tourOpen}
        steps={TOUR_STEPS}
        onClose={() => setTourOpen(false)}
        onComplete={completeTour}
      />
      <div className="mt-6">
        <VideoExplicativeCard />
      </div>
    </div>
  );
}

// Banner persistent: raport ore semnat LIPSĂ (rămâne până e încărcat)
function ReportReminderBanner() {
  const [missing, setMissing] = useState([]);
  useEffect(() => {
    api.get("/me/report-reminder").then((r) => setMissing(r.data?.missing || [])).catch(() => {});
  }, []);
  if (missing.length === 0) return null;
  return (
    <div className="card-flat p-4 border-l-4 border-l-red-500 ring-2 ring-red-500/40 animate-pulse" data-testid="report-reminder-banner">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-red-500/20 grid place-items-center text-xl shrink-0">📎</div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-red-400 font-bold">
            Raport ore semnat LIPSĂ — încarcă-l cât mai repede!
          </div>
          <div className="mt-1.5 flex flex-col gap-1">
            {missing.map((m, i) => (
              <div key={i} className="text-sm text-white" data-testid={`report-missing-${m.regie_id}`}>
                <strong>{m.regie_name}</strong>
                <span className="text-white/50 text-xs"> · săptămâna {fmtRoDate(m.week_start)} – {fmtRoDate(m.week_end)}</span>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-white/60 mt-1.5">
            Deschide <strong className="text-white/80">„Adaugă ore"</strong>, atinge „📷 Fă poză" sau „🖼 Din galerie" și atașează raportul semnat de Vorarbeiter.
          </div>
        </div>
      </div>
    </div>
  );
}

// Banner: mașina alocată AZI conform planificatorului (deasupra cardurilor de acțiuni)
function TodayCarBanner() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const t = new Date();
    const iso = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
    api.get("/me/day", { params: { date_str: iso } }).then((r) => setData(r.data)).catch(() => setData({ entries: [] }));
  }, []);
  if (!data) return null;
  const entries = data.entries || [];
  const seen = new Set();
  const cars = [];
  for (const e of entries) {
    for (const c of (e.cars || [])) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      cars.push({ ...c, where: e.label });
    }
  }
  if (cars.length === 0) return null;
  return (
    <div className="card-flat p-4 border-l-4 border-l-emerald-500" data-testid="emp-today-car-banner">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-emerald-500/15 grid place-items-center text-xl shrink-0">🚗</div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-bold">
            Mașina ta azi · conform planificatorului
          </div>
          <div className="mt-1.5 flex flex-col gap-1.5">
            {cars.map((c) => (
              <div key={c.id} className="flex items-center gap-2 flex-wrap" data-testid={`emp-today-car-${c.id}`}>
                <span className="font-display font-bold text-white text-base">
                  {[c.marca, c.model].filter(Boolean).join(" ") || "Mașină"}
                </span>
                {c.culoare && <span className="text-sm text-white/70 capitalize">{c.culoare}</span>}
                {c.plate && <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-white/10 text-white/80">{c.plate}</span>}
                {c.last_driver_name && (
                  <span className="text-[11px] text-white/50" data-testid={`emp-today-car-lastdriver-${c.id}`}>
                    (ultimul șofer: <strong className="text-white/75">{c.last_driver_name}</strong>{c.last_driver_date ? ` · ${fmtRoDate(c.last_driver_date)}` : ""})
                  </span>
                )}
                {c.is_me_driver && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                    🔑 TU EȘTI ȘOFERUL
                  </span>
                )}
                {!c.is_me_driver && c.driver_name && (
                  <span className="text-[11px] text-white/50">🔑 șofer: {c.driver_name}</span>
                )}
                {c.where && <span className="text-[11px] text-white/40">· 📍 {c.where}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TomorrowBanner({ onOpenPlan }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/me/day").then((r) => setData(r.data)).catch(() => setData({ entries: [] }));
  }, []);
  if (!data) return null;
  const entries = data.entries || [];
  const dt = data.date ? new Date(data.date) : null;
  const dayLabel = dt ? dt.toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long" }) : "mâine";

  if (entries.length === 0) {
    return (
      <button
        type="button"
        onClick={onOpenPlan}
        className="card-flat p-4 w-full text-left flex items-center gap-3 hover:border-zinc-400 transition-colors"
        data-testid="emp-tomorrow-banner"
      >
        <div className="w-10 h-10 rounded-md bg-white/5 grid place-items-center text-lg">📅</div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">Mâine, {dayLabel}</div>
          <div className="text-sm text-white/70 mt-0.5">Nu ai nicio alocare. <span className="text-red-500 font-semibold">Vezi planul săptămânii →</span></div>
        </div>
      </button>
    );
  }

  const main = entries[0];
  const extra = entries.length - 1;
  const color = main.color || (main.is_regie ? "#F59E0B" : "#3B82F6");

  return (
    <button
      type="button"
      onClick={onOpenPlan}
      className="card-flat p-4 w-full text-left hover:shadow-md transition-all border-l-4 group"
      style={{ borderLeftColor: color }}
      data-testid="emp-tomorrow-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md grid place-items-center text-xl shrink-0" style={{ background: `${color}20`, color }}>
          {main.is_regie ? "🔧" : "📋"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color }}>
            Mâine, {dayLabel}
            {main.is_regie && <span className="ml-2 px-1.5 py-0.5 text-[9px] rounded bg-amber-100 text-amber-800">REGIE</span>}
          </div>
          <div className="font-display text-lg font-bold mt-0.5 truncate text-white">{main.label}</div>
          {main.project?.adresa && <div className="text-xs text-white/60 mt-1">📍 {main.project.adresa}</div>}
          {main.project?.work_zones && <div className="text-xs text-white/50">🔨 {main.project.work_zones}</div>}
          {main.project?.special && <div className="text-xs text-amber-400 mt-1">⚠ {main.project.special}</div>}
          {main.colleagues?.length > 0 && (
            <div className="text-[11px] text-white/50 mt-2 flex items-center gap-1 flex-wrap">
              <UsersIcon size={11}/>
              <span>cu</span>
              {main.colleagues.slice(0, 4).map((c) => (
                <span key={c.id} className="px-1.5 py-0.5 rounded bg-white/10 text-white/80">
                  {c.color && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{ background: c.color }}/>}
                  {c.name}
                </span>
              ))}
              {main.colleagues.length > 4 && <span className="text-white/30">+{main.colleagues.length - 4}</span>}
            </div>
          )}
          {main.cars?.length > 0 && (
            <div className="text-[11px] mt-2 flex items-center gap-1 flex-wrap">
              <span className="text-white/50">🚗</span>
              {main.cars.map((c) => (
                <span
                  key={c.id}
                  className={`px-1.5 py-0.5 rounded font-medium ${c.is_me_driver ? "bg-amber-400 text-amber-900 font-bold" : "bg-white/10 text-white/85"}`}
                  title={`${c.marca || ""} ${c.model || ""}`.trim() + (c.driver_name ? ` · șofer: ${c.driver_name}` : "")}
                >
                  {c.is_me_driver && "🔑 "}
                  {c.plate || "—"}
                  {c.driver_name && !c.is_me_driver && <span className="ml-1 text-white/50">({c.driver_name.split(" ")[0]})</span>}
                </span>
              ))}
            </div>
          )}
          {extra > 0 && (
            <div className="text-[11px] text-white/40 mt-1.5">+ încă {extra} alocare{extra === 1 ? "" : "i"} mâine — click pentru toate</div>
          )}
        </div>
      </div>
    </button>
  );
}

function TasksBanner({ onOpen }) {
  const [tasks, setTasks] = useState(null);
  useEffect(() => {
    api.get("/tasks?status=pending")
      .then((r) => setTasks(r.data || []))
      .catch(() => setTasks([]));
  }, []);
  if (!tasks) return null;
  if (tasks.length === 0) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="card-flat p-4 w-full text-left flex items-center gap-3 hover:border-zinc-400 transition-colors"
        data-testid="emp-tasks-banner"
      >
        <div className="w-10 h-10 rounded-md bg-zinc-100 grid place-items-center">📝</div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Task-uri primite</div>
          <div className="text-sm text-zinc-600">Niciun task activ. <span className="text-zinc-400">Click pentru istoric →</span></div>
        </div>
      </button>
    );
  }
  // Newest first (created_at desc — already sorted by API). Use first as "main"
  const main = tasks[0];
  const extra = tasks.length - 1;
  const color = "#16A34A";
  return (
    <button
      type="button"
      onClick={onOpen}
      className="card-flat p-4 w-full text-left hover:shadow-md transition-all border-l-4 group"
      style={{ borderLeftColor: color }}
      data-testid="emp-tasks-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md grid place-items-center text-xl shrink-0" style={{ background: `${color}20`, color }}>
          📝
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-2" style={{ color }}>
            <span>Task-uri primite</span>
            <span className="px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold animate-pulse">
              {tasks.length}
            </span>
          </div>
          <div className="font-display text-lg font-bold mt-0.5 truncate">{main.title}</div>
          {main.created_by_name && (
            <div className="text-xs text-zinc-600 mt-1">de la <strong>{main.created_by_name}</strong></div>
          )}
          {main.due_date && (
            <div className="text-xs text-amber-700 mt-0.5">⏰ Termen: {main.due_date}</div>
          )}
          {extra > 0 && (
            <div className="text-[11px] text-zinc-400 mt-1.5">
              + încă {extra} task{extra === 1 ? "" : "-uri"} — click pentru toate
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

function TasksModal({ onClose }) {
  const [tasks, setTasks] = useState(null);
  const [completing, setCompleting] = useState(null);

  const load = () => {
    api.get("/tasks")
      .then((r) => setTasks(r.data || []))
      .catch(() => setTasks([]));
  };
  useEffect(() => { load(); }, []);

  if (!tasks) {
    return <ModalShell title="📝 Task-urile mele" onClose={onClose}><div className="text-zinc-400">Se încarcă…</div></ModalShell>;
  }

  const pending = tasks.filter((t) => t.status === "pending");
  const done = tasks.filter((t) => t.status === "done");

  return (
    <ModalShell title="📝 Task-urile mele" onClose={onClose} maxW="max-w-2xl">
      {tasks.length === 0 ? (
        <div className="text-center py-8 text-sm text-zinc-500">
          Niciun task primit. Adminul îți va atribui task-uri când e cazul.
        </div>
      ) : (
        <div className="space-y-4">
          {pending.length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-bold text-zinc-500 mb-2">În așteptare ({pending.length})</div>
              <div className="space-y-2">
                {pending.map((t) => (
                  <div key={t.id} className="card-flat p-3 border-l-4 border-l-[#16A34A]" data-testid={`emp-task-${t.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-sm">{t.title}</div>
                        {t.description && (
                          <div className="text-xs text-zinc-600 mt-1 whitespace-pre-wrap">{t.description}</div>
                        )}
                        {(t.attachments || []).length > 0 && (
                          <div className="mt-2" data-testid={`emp-task-attachments-${t.id}`}>
                            <TaskAttachmentList attachments={t.attachments} testPrefix={`emp-task-att-${t.id}`} />
                          </div>
                        )}
                        <div className="text-[11px] text-zinc-500 mt-2 flex flex-wrap gap-x-3 gap-y-1">
                          {t.created_by_name && <span>👤 de la <strong>{t.created_by_name}</strong></span>}
                          {t.created_at && <span>📅 {String(t.created_at).slice(0, 10)}</span>}
                          {t.due_date && <span className="text-amber-700 font-semibold">⏰ Termen: {fmtRoDate(t.due_date)}</span>}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => setCompleting(t)}
                        className="bg-[#16A34A] hover:bg-[#15803D] shrink-0"
                        data-testid={`emp-task-complete-${t.id}`}
                      >
                        <Check size={14}/>
                        Efectuat
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {done.length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-bold text-zinc-500 mb-2">Efectuate ({done.length})</div>
              <div className="space-y-2">
                {done.map((t) => (
                  <div key={t.id} className="card-flat p-3 opacity-70 bg-zinc-50">
                    <div className="font-display font-bold text-sm line-through text-zinc-500">{t.title}</div>
                    {t.completed_at && (
                      <div className="text-[10px] text-emerald-700 mt-1">✓ {String(t.completed_at).slice(0, 10)}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {completing && (
        <CompleteTaskDialog
          task={completing}
          onClose={() => setCompleting(null)}
          onSaved={() => { setCompleting(null); load(); }}
        />
      )}
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children, maxW = "max-w-2xl", swissBlack = false }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`${maxW} max-h-[90vh] overflow-y-auto ${swissBlack ? "sb-modal" : ""}`}>
        <DialogHeader>
          <DialogTitle className={swissBlack ? "text-xl font-extrabold tracking-tight" : ""}>
            {swissBlack && (
              <div className="sb-marker-row"><span className="num">// {title.toUpperCase()}</span><span className="seg"/></div>
            )}
            {!swissBlack && title}
          </DialogTitle>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

function CarsViewModal({ onClose }) {
  const [cars, setCars] = useState(null);
  const [sel, setSel] = useState(null); // mașina selectată
  const [tab, setTab] = useState("drivers"); // drivers | fuel
  const [drivers, setDrivers] = useState(null);
  const [fuel, setFuel] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/me/cars").then((r) => setCars(r.data || [])).catch(() => setCars([]));
  }, []);
  useEffect(() => {
    if (!sel) return;
    setDrivers(null); setFuel(null);
    api.get(`/cars/${sel.id}/daily-driver-history`).then((r) => setDrivers(r.data?.rows || [])).catch(() => setDrivers([]));
    api.get(`/cars/${sel.id}/fuel`).then((r) => setFuel(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => setFuel([]));
  }, [sel]);
  const carName = (c) => `${c.numar_inmatriculare || ""} · ${[c.marca, c.model].filter(Boolean).join(" ")}`.trim().replace(/^·\s*/, "");
  const filtered = (cars || []).filter((c) => !q || carName(c).toLowerCase().includes(q.toLowerCase()));
  return (
    <ModalShell title={sel ? `🚗 ${carName(sel)}` : "🚗 Autoturisme"} onClose={onClose} maxW="max-w-2xl">
      {!sel ? (
        <>
          <Input placeholder="Caută mașină…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cars-view-search"/>
          {!cars ? <div className="text-zinc-400 text-sm py-4">Se încarcă…</div> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              {filtered.map((c) => (
                <button key={c.id} onClick={() => { setSel(c); setTab("drivers"); }}
                  className="text-left border border-zinc-200 rounded-lg p-3 hover:border-[var(--primary)] hover:bg-zinc-50 transition-colors"
                  data-testid={`cars-view-car-${c.id}`}>
                  <div className="font-bold text-sm">{c.numar_inmatriculare || "—"}</div>
                  <div className="text-xs text-zinc-500">{[c.marca, c.model].filter(Boolean).join(" ") || "—"}</div>
                </button>
              ))}
              {filtered.length === 0 && <div className="text-zinc-400 text-sm italic col-span-2 py-4 text-center">Nicio mașină.</div>}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <button onClick={() => setSel(null)} className="text-xs text-[var(--primary)] hover:underline" data-testid="cars-view-back">← Înapoi la listă</button>
            <div className="flex rounded-lg border border-zinc-200 overflow-hidden ml-auto text-xs font-semibold">
              <button onClick={() => setTab("drivers")} className={`px-3 py-1.5 ${tab === "drivers" ? "bg-[var(--primary)] text-white" : "bg-white text-zinc-600"}`} data-testid="cars-view-tab-drivers">Istoric șoferi</button>
              <button onClick={() => setTab("fuel")} className={`px-3 py-1.5 border-l border-zinc-200 ${tab === "fuel" ? "bg-[var(--primary)] text-white" : "bg-white text-zinc-600"}`} data-testid="cars-view-tab-fuel">Alimentări</button>
            </div>
          </div>
          {tab === "drivers" && (
            !drivers ? <div className="text-zinc-400 text-sm py-4">Se încarcă…</div> : (
              <div className="space-y-1 max-h-[55vh] overflow-y-auto" data-testid="cars-view-drivers-list">
                {drivers.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs border-b border-zinc-100 py-1.5">
                    <span className="tabular-nums text-zinc-500 w-20 flex-shrink-0">{fmtRoDate(r.date)}</span>
                    <span className="font-semibold flex-1 min-w-0 truncate">{r.driver_name || "—"}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded text-white truncate max-w-[40%]" style={{ background: r.color || "#71717A" }}>{r.project_name || r.regie_name || r.label || "—"}</span>
                  </div>
                ))}
                {drivers.length === 0 && <div className="text-zinc-400 text-sm italic py-4 text-center">Fără istoric.</div>}
              </div>
            )
          )}
          {tab === "fuel" && (
            !fuel ? <div className="text-zinc-400 text-sm py-4">Se încarcă…</div> : (
              <div className="space-y-1 max-h-[55vh] overflow-y-auto" data-testid="cars-view-fuel-list">
                {fuel.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs border-b border-zinc-100 py-1.5 flex-wrap">
                    <span className="tabular-nums text-zinc-500 w-20 flex-shrink-0">{fmtRoDate(f.data_alimentare)}</span>
                    <span className="font-semibold flex-1 min-w-0 truncate">{f.person_name || f.alimentat_de || "—"}</span>
                    <span className="tabular-nums">{f.litri ? `${f.litri} L` : "—"}</span>
                    <span className="tabular-nums text-zinc-500">{f.numar_km ? `${f.numar_km} km` : ""}</span>
                    {f.consum != null && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">{f.consum} L/100km</span>}
                  </div>
                ))}
                {fuel.length === 0 && <div className="text-zinc-400 text-sm italic py-4 text-center">Fără alimentări.</div>}
              </div>
            )
          )}
        </>
      )}
    </ModalShell>
  );
}

function PlanModal({ onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/me/week").then((r) => setData(r.data)).catch(() => setData({ days: [] }));
  }, []);
  if (!data) return <ModalShell title="Plan săptămânal" onClose={onClose}><div className="text-zinc-400">Se încarcă…</div></ModalShell>;
  const dayNames = ["Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă"];
  return (
    <ModalShell title="📋 Planul meu săptămânal" onClose={onClose} maxW="max-w-3xl">
      <div className="text-xs text-zinc-500 mb-3">Săptămâna începe {data.week_start}</div>
      <div className="space-y-3">
        {data.days?.map((day, i) => (
          <div key={day.date} className="card-flat p-3" data-testid={`plan-day-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-bold text-sm">{dayNames[i]} <span className="text-zinc-400 font-normal">· {day.date}</span></div>
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                {day.entries.length === 0 ? "Liber" : `${day.entries.length} alocare`}
              </span>
            </div>
            {day.entries.length === 0 ? (
              <div className="text-xs text-zinc-400 italic">Nicio alocare.</div>
            ) : (
              <div className="space-y-2">
                {day.entries.map((e) => (
                  <div key={e.id} className="border-l-4 pl-3 py-1" style={{ borderColor: e.color || "#3B82F6" }}>
                    <div className="font-semibold text-sm flex items-center gap-2">
                      {e.is_regie && <span className="px-1.5 py-0.5 text-[9px] rounded bg-amber-100 text-amber-800 font-bold">REGIE</span>}
                      {e.label}
                    </div>
                    {e.project && (
                      <div className="text-[11px] text-zinc-600 mt-1">
                        {e.project.adresa && <div>📍 {e.project.adresa}</div>}
                        {e.project.work_zones && <div>🔨 {e.project.work_zones}</div>}
                        {e.project.zones?.length > 0 && (
                          <div>📦 {e.project.zones.map((z) => `${z.count}× ${z.type}`).join(", ")}</div>
                        )}
                        {e.project.special && <div className="text-amber-700">⚠ {e.project.special}</div>}
                      </div>
                    )}
                    {e.colleagues?.length > 0 && (
                      <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1 flex-wrap">
                        <UsersIcon size={11}/>
                        {e.colleagues.map((c) => (
                          <span key={c.id} className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700">
                            {c.color && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{ background: c.color }}/>}
                            {c.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {e.cars?.length > 0 && (
                      <div className="text-[11px] mt-1 flex items-center gap-1 flex-wrap">
                        <span className="text-zinc-500">🚗</span>
                        {e.cars.map((c) => (
                          <span
                            key={c.id}
                            className={`px-1.5 py-0.5 rounded font-medium ${c.is_me_driver ? "bg-amber-100 text-amber-900 border border-amber-300 font-bold" : "bg-blue-50 text-blue-800 border border-blue-200"}`}
                            title={`${c.marca || ""} ${c.model || ""}`.trim() + (c.driver_name ? ` · șofer: ${c.driver_name}` : "")}
                          >
                            {c.is_me_driver && "🔑 "}
                            {c.plate || "—"}
                            {c.driver_name && !c.is_me_driver && <span className="ml-1 text-zinc-500">({c.driver_name.split(" ")[0]})</span>}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </ModalShell>
  );
}

function HoursModal({ onClose }) {
  const { user } = useAuth();
  // Bypass alocare Planificator: admin sau user cu flag can_log_without_allocation.
  // Cei care au bypass pot introduce ore fără să fie alocați + văd toate proiectele/regiile în dropdown.
  const hasAllocationBypass = (user?.role || "").toLowerCase() === "admin" || !!user?.can_log_without_allocation;
  // Toți angajații văd echipa de azi + pot adăuga colegi ad-hoc.
  // (Anterior era gated prin `team_hours_entry_enabled`, dar Mancau Petrică nu putea
  // adăuga ore pentru cei cu care a lucrat — acum deschidem pentru toți.)
  const teamMode = true;
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [project, setProject] = useState("__none__");
  const [colleagues, setColleagues] = useState([]);
  const [meIsDriver, setMeIsDriver] = useState(false);
  const [meDrivenCars, setMeDrivenCars] = useState([]);
  const [myHoursLogged, setMyHoursLogged] = useState(null); // { exists, by_self, submitted_by_name, hours, ... }
  const [projectsInfo, setProjectsInfo] = useState([]);
  const [allProjectsRegii, setAllProjectsRegii] = useState({ projects: [], regii: [], length: 0 });
  const [availableCars, setAvailableCars] = useState([]);
  const [carId, setCarId] = useState("");
  // Tabel ore per persoană
  const [rows, setRows] = useState([]); // [{ person_id, name, hours, hours_driven, absent }]
  const [myId, setMyId] = useState(null);
  const [activePersons, setActivePersons] = useState([]); // pentru „adaugă ad-hoc"
  // Status ore deja-înregistrate pentru fiecare persoană din tabel (per data curentă)
  // { [person_id]: { exists, hours, hours_driven, by_self, submitted_by_name, project_label } }
  const [personsHoursStatus, setPersonsHoursStatus] = useState({});
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  // Prompt saci după salvarea orelor (doar la PROIECTE)
  const [bagsPrompt, setBagsPrompt] = useState(null); // { projectId, projectName }
  // Raport ore semnat (zilnic / săptămânal) — obligatoriu la regii cel târziu vineri seara
  const [reportFile, setReportFile] = useState(null);
  const [reportType, setReportType] = useState("tages"); // "tages" | "wochen"
  const [reportEndDate, setReportEndDate] = useState(""); // doar pentru wochen

  useEffect(() => {
    api.get("/auth/me").then((r) => setMyId(r.data.person_id || r.data.id)).catch(() => {});
    // Endpoint lightweight care permite oricărui user să vadă toți colegii activi
    // (folosit pentru dropdown-ul „adaugă persoană ad-hoc" — nu necesită PERSONAL.view).
    api.get("/me/active-coworkers").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setActivePersons(list);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!date) return;
    api.get(`/me/colleagues?date_str=${date}`).then((r) => {
      try {
        const data = r?.data || {};
        // Defensive: every colleague trebuie să aibă un id valid (string), altfel îl filtrăm.
        const rawCols = Array.isArray(data.colleagues) ? data.colleagues : [];
        const seenCol = new Set();
        const cols = [];
        for (const c of rawCols) {
          const cid = c && c.id ? String(c.id) : null;
          if (!cid || seenCol.has(cid)) continue;
          seenCol.add(cid);
          cols.push({
            id: cid,
            name: c.name || cid,
            color: c.color,
            is_driver: !!c.is_driver,
            driven_cars: Array.isArray(c.driven_cars) ? c.driven_cars : [],
          });
        }
        setColleagues(cols);
        setMeIsDriver(!!data.me_is_driver);
        setMeDrivenCars(Array.isArray(data.me_driven_cars) ? data.me_driven_cars : []);
        setMyHoursLogged(data.my_hours_already_logged || null);
        // Defensive: filter entries without id, dedupe pe id ca să nu duplicăm SelectItem
        // (un Radix SelectItem duplicat sau cu value="" poate arunca eroare → pagină albă).
        const rawPi = Array.isArray(data.projects_info) ? data.projects_info : [];
        const seen = new Set();
        const cleanPi = [];
        for (const p of rawPi) {
          const pid = (p && (p.id || p.regie_id || p.project_id)) || "";
          if (!pid || seen.has(pid)) continue;
          seen.add(pid);
          cleanPi.push({ id: String(pid), label: p.label || "—", is_regie: !!p.is_regie });
        }
        setProjectsInfo(cleanPi);
        // Fetch listă completă proiecte + regii pentru useri cu bypass alocare
        if (hasAllocationBypass) {
          api.get(`/admin/projects-regii-list?day=${date}`).then((pr) => {
            setAllProjectsRegii({
              projects: pr?.data?.projects || [],
              regii: pr?.data?.regii || [],
              length: (pr?.data?.projects?.length || 0) + (pr?.data?.regii?.length || 0),
            });
          }).catch(() => { /* silent */ });
        }
        setAvailableCars(Array.isArray(data.cars) ? data.cars : []);
        if (cleanPi.length === 1 && cleanPi[0].id) {
          setProject(cleanPi[0].id);
        }
      } catch (err) {
        console.error("[HoursModal] Failed to process /me/colleagues response", err);
        try {
          import("../components/ErrorBoundary").then(({ reportErrorToBackend }) => {
            reportErrorToBackend({
              message: err?.message || "HoursModal colleagues processing failed",
              stack: err?.stack || "",
              context: "HoursModal:/me/colleagues",
              extra: { date },
            });
          }).catch(() => {});
        } catch { /* noop */ }
        // Fallback graceful: setări sigure ca să nu rămână modal-ul stricat
        setColleagues([]);
        setMeIsDriver(false);
        setMeDrivenCars([]);
        setProjectsInfo([]);
        setAvailableCars([]);
      }
    }).catch((err) => {
      // Network / 4xx / 5xx — nu blocăm UI-ul, doar logăm
      console.warn("[HoursModal] /me/colleagues failed:", err?.message);
    });
  }, [date]);

  // Sincronizează rânduri când se schimbă lista colleagues sau myId
  useEffect(() => {
    if (!myId) return;
    setRows((prev) => {
      const existingMap = new Map(prev.map((r) => [r.person_id, r]));
      const merged = [];
      // Self mereu primul rând (cu flag is_driver din meIsDriver)
      const selfExisting = existingMap.get(myId);
      const self = selfExisting || { person_id: myId, name: "Tu", hours: "", hours_driven: "", absent: false, is_self: true };
      merged.push({
        ...self,
        name: "Tu",
        is_self: true,
        is_driver: meIsDriver,
        driven_cars: meDrivenCars,
      });
      // Pentru muncitori normali (teamMode === false) afișăm doar rândul propriu.
      // Tot ce ține de colegi/ad-hoc e ascuns intenționat.
      if (teamMode) {
        // Colegi planificați
        for (const c of colleagues) {
          if (c.id === myId) continue;
          const r = existingMap.get(c.id) || { person_id: c.id, name: c.name, hours: "", hours_driven: "", absent: false };
          merged.push({
            ...r,
            name: c.name,
            is_driver: !!c.is_driver,
            driven_cars: Array.isArray(c.driven_cars) ? c.driven_cars : [],
          });
        }
        // Persoane ad-hoc (deja adăugate manual)
        for (const r of prev) {
          if (r.person_id === myId) continue;
          if (colleagues.some((c) => c.id === r.person_id)) continue;
          merged.push(r);
        }
      }
      return merged;
    });
  }, [colleagues, myId, meIsDriver, meDrivenCars, teamMode]);

  const updateRow = (pid, patch) => setRows((arr) => arr.map((r) => r.person_id === pid ? { ...r, ...patch } : r));
  const removeRow = (pid) => setRows((arr) => arr.filter((r) => r.person_id !== pid));

  // Ore suplimentare pe ALT șantier decât cel alocat
  const [extraOpen, setExtraOpen] = useState(false);
  const [extraSites, setExtraSites] = useState(null);
  const [extraSite, setExtraSite] = useState("");
  const [extraHours, setExtraHours] = useState("");
  const [extraDriven, setExtraDriven] = useState("");
  const [extraCoworkers, setExtraCoworkers] = useState([]);
  useEffect(() => { setExtraSites(null); setExtraSite(""); }, [date]);

  // Fetch status ore-deja-înregistrate pentru toți colegii (non-self) la fiecare schimbare de dată sau persoane.
  useEffect(() => {
    const others = rows.map((r) => r.person_id).filter((id) => id && id !== myId);
    if (!date || others.length === 0) {
      setPersonsHoursStatus({});
      return;
    }
    let cancelled = false;
    api.post("/me/persons-hours-status", { date, person_ids: others }).then((r) => {
      if (!cancelled) setPersonsHoursStatus(r?.data || {});
    }).catch(() => { if (!cancelled) setPersonsHoursStatus({}); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, rows.map((r) => r.person_id).join("|"), myId]);

  const addAdHoc = (p) => {
    if (rows.some((r) => r.person_id === p.id)) { toast.info(`${p.name} e deja în tabel`); return; }
    setRows((arr) => [...arr, { person_id: p.id, name: p.name, hours: "", hours_driven: "", absent: false, ad_hoc: true }]);
  };

  // Indiciu ore doar în contextul șantierului selectat (nu pentru toată lumea)
  const siteHint = (pid, list) => {
    const x = (list || []).find((r) => r.person_id === pid);
    if (!x) return "";
    return ` — ⏱ are ${x.hours}h${x.hours_driven > 0 ? ` +🚗${x.hours_driven}h` : ""} pe acest șantier`;
  };

  // Nealocat: poate totuși alege un șantier (marcat pentru verificare de echipă)
  const [selfSites, setSelfSites] = useState(null);
  const [selfDeclaredSite, setSelfDeclaredSite] = useState("");
  useEffect(() => { setSelfDeclaredSite(""); setSelfSites(null); }, [date]);

  // Ore deja pontate pe șantierul/regia selectată — vizibil pentru toți
  const [siteLogged, setSiteLogged] = useState([]);
  useEffect(() => {
    const info = projectsInfo.find((x) => x.id === project);
    let qs = null;
    if (info) qs = `${info.is_regie ? "regie_id" : "project_id"}=${info.id}`;
    else if (selfDeclaredSite && selfSites) {
      const isReg = (selfSites.regii || []).some((r) => r.id === selfDeclaredSite);
      qs = `${isReg ? "regie_id" : "project_id"}=${selfDeclaredSite}&declared=1`;
    }
    if (!date || !qs) { setSiteLogged([]); return; }
    let c = false;
    api.get(`/me/site-day-hours?day=${date}&${qs}`)
      .then((r) => { if (!c) setSiteLogged(r.data || []); })
      .catch((err) => {
        if (!c) setSiteLogged([]);
        const msg = err?.response?.data?.detail;
        if (msg && (err?.response?.status === 429 || err?.response?.status === 403)) toast.warning(msg);
      });
    return () => { c = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, project, projectsInfo.length, selfDeclaredSite, selfSites]);
  const [extraLogged, setExtraLogged] = useState([]);
  useEffect(() => {
    if (!date || !extraSite || !extraSites) { setExtraLogged([]); return; }
    const isRegie = (extraSites.regii || []).some((x) => x.id === extraSite);
    let c = false;
    api.get(`/me/site-day-hours?day=${date}&${isRegie ? "regie_id" : "project_id"}=${extraSite}&declared=1`)
      .then((r) => { if (!c) setExtraLogged(r.data || []); })
      .catch((err) => {
        if (!c) setExtraLogged([]);
        const msg = err?.response?.data?.detail;
        if (msg && (err?.response?.status === 429 || err?.response?.status === 403)) toast.warning(msg);
      });
    return () => { c = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, extraSite, extraSites]);
  const confirmDeclared = async (x, valid, siteRef, setter) => {
    try {
      await api.post("/me/site-day-hours/confirm", { person_id: x.person_id, day: date, ...siteRef, valid });
      setter((list) => list.map((r) => r.person_id === x.person_id ? { ...r, self_declared_status: valid ? "confirmed" : "disputed" } : r));
      toast.success(valid ? "✔ Confirmat — a lucrat aici" : "✖ Semnalat ca incorect — adminul va verifica");
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare la confirmare"); }
  };
  const renderSiteLogged = (list, testId, siteRef, setter) => (list.length === 0 ? null : (
    <div className="border border-sky-400/40 bg-sky-500/10 rounded-md p-2.5" data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-sky-200 mb-1">👀 Ore deja pontate pe acest șantier în această zi</div>
      <div className="flex flex-col gap-0.5">
        {list.map((x) => (
          <div key={x.person_id} className="text-[11.5px] text-sky-100 flex items-center gap-2 flex-wrap" data-testid={`${testId}-row-${x.person_id}`}>
            <span>
              <b>{x.person_name}</b> — <b>{x.hours}h</b>
              {x.hours_driven > 0 && <> +🚗{x.hours_driven}h</>}
              {x.logged_by_name && <span className="text-sky-200/70"> · pontat de {x.logged_by_name}</span>}
            </span>
            {x.self_declared && (
              <>
                <span className="text-[9px] px-1 rounded bg-orange-500/30 text-orange-200 font-bold uppercase border border-orange-400/50">⚠ adăugat singur</span>
                {x.self_declared_status === "confirmed" && <span className="text-[10px] text-emerald-300 font-bold">✔ confirmat</span>}
                {x.self_declared_status === "disputed" && <span className="text-[10px] text-red-300 font-bold">✖ contestat — admin verifică</span>}
                {!x.self_declared_status && siteRef && setter && (
                  <span className="inline-flex gap-1">
                    <button type="button" onClick={() => confirmDeclared(x, true, siteRef, setter)} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`${testId}-confirm-${x.person_id}`}>✔ Corect</button>
                    <button type="button" onClick={() => confirmDeclared(x, false, siteRef, setter)} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-600 text-white hover:bg-red-700" data-testid={`${testId}-dispute-${x.person_id}`}>✖ Nu a lucrat</button>
                  </span>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  ));

  // Listă persoane active care NU sunt deja în tabel — sortate alfabetic
  const availableAdHocPersons = useMemo(() => {
    const inTable = new Set(rows.map((r) => r.person_id));
    return activePersons
      .filter((p) => !inTable.has(p.id))
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "ro"));
  }, [activePersons, rows]);

  const submit = async () => {
    setErrorMsg("");
    // Validare locală: fiecare rând (persoană) TREBUIE completat fizic, chiar și cu 0.
    // Se ignoră rândurile deja înregistrate de altcineva (nu vor fi trimise).
    const rowsRequiringInput = rows.filter((r) => {
      if (!r.is_self) {
        const st = personsHoursStatus[r.person_id];
        if (st?.exists && !r.override) return false; // sărit — nu trebuie completat
      }
      return true;
    });
    const missing = rowsRequiringInput.filter((r) => {
      if (r.absent) return false; // absent OK, e echivalent cu 0
      const h = String(r.hours ?? "").trim();
      const d = String(r.hours_driven ?? "").trim();
      // Rândul TREBUIE să aibă cel puțin unul dintre câmpuri completat (fie și cu 0)
      return h === "" && d === "";
    });
    if (missing.length > 0) {
      const names = missing.map((r) => r.name).slice(0, 3).join(", ");
      const more = missing.length > 3 ? ` +${missing.length - 3}` : "";
      setErrorMsg(`Completează orele (poate fi 0) sau bifează Absent pentru: ${names}${more}`);
      return;
    }
    // Validare locală: cel puțin un rând cu ore > 0 sau marcat absent
    const productive = rows.filter((r) => (parseFloat(r.hours) || 0) > 0 || (parseFloat(r.hours_driven) || 0) > 0 || r.absent);
    if (productive.length === 0) { setErrorMsg("Adaugă cel puțin o persoană cu ore sau absență"); return; }
    // Blocare ore pentru viitor (non-admin): doar trecut sau ziua curentă.
    const isAdminForDate = (user?.role || "").toLowerCase() === "admin";
    if (!isAdminForDate && date > today) {
      setErrorMsg("Nu poți completa ore pentru date viitoare. Doar trecut sau ziua curentă.");
      return;
    }
    // Fix bug „ore fără regie/proiect": dacă cineva are ore > 0 (lucrate SAU conduse),
    // TREBUIE selectat un proiect sau regie. Absența singură e OK fără activitate.
    // BYPASS admin (Iter 92): adminul poate crea intrări generice pentru situații
    // excepționale (ex: nu găsește regia potrivită, vrea să o completeze ulterior).
    const isAdminUser = hasAllocationBypass;
    const hasAnyHours = rows.some((r) => (parseFloat(r.hours) || 0) > 0 || (parseFloat(r.hours_driven) || 0) > 0);
    if (hasAnyHours && project === "__none__" && !isAdminUser && !selfDeclaredSite) {
      setErrorMsg("Trebuie să alegi PROIECTUL sau REGIA pentru care se înregistrează orele. Fără activitate nu se pot introduce ore.");
      return;
    }
    // Validare raport semnat — dacă e wochen, end_date obligatoriu și >= start
    if (reportFile && reportType === "wochen") {
      if (!reportEndDate) { setErrorMsg("Raport săptămânal: alege data de sfârșit a săptămânii"); return; }
      if (reportEndDate < date) { setErrorMsg("Data de sfârșit a raportului trebuie să fie >= data start"); return; }
    }
    // Validare ore suplimentare pe alt șantier
    const _extraH = parseFloat(extraHours) || 0;
    const extraActive = extraOpen && (_extraH > 0 || extraCoworkers.some((c) => (parseFloat(c.hours) || 0) > 0));
    if (extraActive && !extraSite) {
      setErrorMsg("Ai completat ore pe ALT șantier dar nu ai ales șantierul din listă.");
      return;
    }
    setBusy(true);
    try {
      // Filtrăm persoanele deja înregistrate (fără override explicit) — NU vrem
      // să suprascriem accidental orele salvate de altcineva.
      const rowsToSend = rows.filter((r) => {
        if (r.is_self) return true;
        const st = personsHoursStatus[r.person_id];
        if (st?.exists && !r.override) return false;
        return true;
      });
      const skipped = rows.length - rowsToSend.length;
      // Dacă TOATE persoanele erau deja înregistrate → afișăm mesaj și ieșim fără request
      if (rowsToSend.length === 0) {
        toast.info(`Toate cele ${skipped} persoane au deja orele înregistrate — nimic de trimis.`);
        setBusy(false);
        onClose();
        return;
      }
      const payload = {
        date,
        car_id: carId || null,
        people: rowsToSend.map((r) => ({
          person_id: r.person_id,
          hours: parseFloat(r.hours) || 0,
          hours_driven: parseFloat(r.hours_driven) || 0,
          absent: !!r.absent,
        })),
      };
      let selectedReg = null;
      let selectedProj = null;
      if (project !== "__none__") {
        const sel = projectsInfo.find((p) => p.id === project);
        if (sel?.is_regie) { payload.regie_id = project; selectedReg = sel; }
        else { payload.project_id = project; selectedProj = sel; }
      } else if (selfDeclaredSite && selfSites) {
        const isReg = (selfSites.regii || []).some((x) => x.id === selfDeclaredSite);
        if (isReg) payload.regie_id = selfDeclaredSite; else payload.project_id = selfDeclaredSite;
        payload.self_declared = true;
      }
      const r = await api.post("/me/quick-time-bulk", payload);
      const warnings = r.data?.warnings || [];
      // Upload raport semnat dacă a fost atașat
      if (reportFile) {
        try {
          const fd = new FormData();
          fd.append("file", reportFile);
          fd.append("report_type", reportType);
          fd.append("start_date", date);
          fd.append("end_date", reportType === "wochen" ? reportEndDate : date);
          if (selectedReg) fd.append("regie_id", selectedReg.id);
          if (selectedProj) fd.append("project_id", selectedProj.id);
          await api.post("/signed-reports", fd, { headers: { "Content-Type": "multipart/form-data" } });
          toast.success(`📎 Raport ore ${reportType === "wochen" ? "săptămânal" : "zilnic"} încărcat`);
        } catch (uerr) {
          toast.error(`Eroare la upload raport: ${uerr?.response?.data?.detail || uerr?.message || "—"}`);
        }
      }
      if (warnings.length > 0) {
        toast.warning(`⚠ ${warnings.length} persoană(e) cu ore din 2+ șantiere — adminul va confirma`);
      } else {
        const savedMsg = `✅ Ore salvate pentru ${r.data?.saved || 0} persoane`;
        toast.success(skipped > 0 ? `${savedMsg} · ${skipped} sărite (deja înregistrate)` : savedMsg);
      }
      // Ore suplimentare pe al doilea șantier
      if (extraActive && extraSite) {
        try {
          const isRegie = (extraSites?.regii || []).some((x) => x.id === extraSite);
          const p2 = {
            date,
            people: [
              { person_id: myId, hours: _extraH, hours_driven: parseFloat(extraDriven) || 0, absent: false },
              ...extraCoworkers
                .filter((c) => (parseFloat(c.hours) || 0) > 0)
                .map((c) => ({ person_id: c.person_id, hours: parseFloat(c.hours) || 0, hours_driven: 0, absent: false })),
            ].filter((p) => (p.hours || 0) > 0 || (p.hours_driven || 0) > 0),
          };
          if (isRegie) p2.regie_id = extraSite; else p2.project_id = extraSite;
          p2.no_overwrite = true;
          const r2 = await api.post("/me/quick-time-bulk", p2);
          const w2 = r2.data?.warnings || [];
          const sk2 = r2.data?.skipped_existing || [];
          toast.success(`✅ Ore suplimentare salvate pe al doilea șantier${w2.length ? " — adminul va confirma zilele cu 2 șantiere" : ""}`);
          if (sk2.length) {
            toast.info(`ℹ ${sk2.map((s) => s.person_name).join(", ")}: aveau deja ore pontate acolo — orele lor NU au fost modificate`);
          }
        } catch (e2) {
          toast.error(`Eroare la orele de pe al doilea șantier: ${e2?.response?.data?.detail || "—"}`);
        }
      }
      // 🗑️ Saci de aruncat — DOAR pe proiecte; dacă un coleg a adăugat deja azi, nu mai întrebăm
      if (selectedProj) {
        try {
          const bp = await api.get("/me/bags-prompt", { params: { project_id: selectedProj.id, date } });
          if (bp.data?.ask) {
            setBagsPrompt({ projectId: selectedProj.id, projectName: selectedProj.name || selectedProj.label || "proiect" });
            setBusy(false);
            return;
          }
        } catch { /* nu blocăm pontajul dacă promptul eșuează */ }
      }
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Eroare la salvare";
      setErrorMsg(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={teamMode ? "⏰ Adaugă ore pentru echipa de azi" : "⏰ Adaugă orele tale"} onClose={onClose} maxW="max-w-3xl" swissBlack>
      {bagsPrompt && (
        <BagsAfterHours projectId={bagsPrompt.projectId} projectName={bagsPrompt.projectName} onDone={onClose} />
      )}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="small-label">Data</Label>
            <Input
              type="date"
              value={date}
              max={(user?.role || "").toLowerCase() === "admin" ? undefined : today}
              onChange={(e) => {
                const v = e.target.value;
                // Pe Android Chrome se poate întâmpla ca picker-ul să dea valori invalide
                // sau goale. Acceptăm doar format YYYY-MM-DD valid; altfel ignorăm.
                if (!v || /^\d{4}-\d{2}-\d{2}$/.test(v)) {
                  setDate(v || today);
                }
              }}
              className="mt-1"
              data-testid="emp-hours-date"
            />
            {(user?.role || "").toLowerCase() !== "admin" && (
              <div className="text-[10px] text-white/60 mt-1">Poți completa doar pentru trecut sau azi.</div>
            )}
          </div>
          {(projectsInfo.length > 0 || hasAllocationBypass) && (
            <div>
              <Label className="small-label">Proiect / Regie <span className="text-red-400">*</span></Label>
              <Select value={project} onValueChange={setProject}>
                <SelectTrigger className={`mt-1 ${project === "__none__" ? "border-red-400 ring-1 ring-red-400/50" : ""}`} data-testid="emp-hours-project"><SelectValue placeholder="⚠ Selectează activitatea"/></SelectTrigger>
                <SelectContent>
                  {/* Adminul + userii cu can_log_without_allocation pot lăsa fără proiect/regie (bypass excepțional) */}
                  {hasAllocationBypass && (
                    <SelectItem value="__none__">— Niciunul —</SelectItem>
                  )}
                  {projectsInfo.length > 0 && (
                    <div className="text-[10px] text-emerald-500 px-2 pt-1 pb-0.5 uppercase font-semibold">Alocări pe această zi</div>
                  )}
                  {projectsInfo.map((p, i) => (
                    <SelectItem key={`${p.id}-${i}`} value={p.id || `__pi_${i}`}>{p.label}{p.is_regie ? " (regie)" : ""}</SelectItem>
                  ))}
                  {hasAllocationBypass && allProjectsRegii.length > 0 && (
                    <>
                      <div className="text-[10px] text-blue-400 px-2 pt-2 pb-0.5 uppercase font-semibold border-t border-white/10 mt-1">📁 Alte proiecte</div>
                      {allProjectsRegii.projects.map((p) => (
                        !projectsInfo.some((x) => x.id === p.id) && (
                          <SelectItem key={`allp-${p.id}`} value={p.id}>{p.name}{p.location ? ` — ${p.location}` : ""}</SelectItem>
                        )
                      ))}
                      <div className="text-[10px] text-amber-400 px-2 pt-2 pb-0.5 uppercase font-semibold border-t border-white/10 mt-1">⚙ Regii</div>
                      {allProjectsRegii.regii.map((r) => (
                        !projectsInfo.some((x) => x.id === r.id) && (
                          <SelectItem key={`allr-${r.id}`} value={r.id}>⚙ {r.name}</SelectItem>
                        )
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
              {project === "__none__" && !hasAllocationBypass && (
                <div className="text-[10px] text-red-300 mt-1 font-semibold" data-testid="emp-hours-project-hint">
                  ⚠ Obligatoriu: alege regia sau proiectul unde s-a lucrat
                </div>
              )}
            </div>
          )}
          {renderSiteLogged(siteLogged, "site-logged-panel", (() => {
            const info = projectsInfo.find((x) => x.id === project);
            if (info) return info.is_regie ? { regie_id: info.id } : { project_id: info.id };
            if (selfDeclaredSite && selfSites) {
              return (selfSites.regii || []).some((r) => r.id === selfDeclaredSite) ? { regie_id: selfDeclaredSite } : { project_id: selfDeclaredSite };
            }
            return null;
          })(), setSiteLogged)}
          {projectsInfo.length === 0 && !hasAllocationBypass && (
            <div className="rounded-md border-2 border-orange-400/60 bg-orange-500/10 p-3 text-white" data-testid="emp-hours-no-allocation">
              <div className="flex items-start gap-2">
                <span className="text-lg leading-none mt-0.5">⚠️</span>
                <div className="flex-1 text-xs leading-relaxed">
                  <div className="font-bold text-orange-200 uppercase tracking-wider text-[11px] mb-1">Nu ești alocat în Planificator pe {date}</div>
                  <div className="text-orange-100/90">
                    Vorbește cu adminul să te aloce la <b>proiectul</b> sau <b>regia</b> la care ai lucrat.
                    Fără alocare NU poți înregistra orele.
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      // Re-fetch colegi/proiecte
                      api.get(`/me/colleagues?date_str=${date}`).then((r) => {
                        const raw = Array.isArray(r?.data?.projects_info) ? r.data.projects_info : [];
                        const seen = new Set();
                        const clean = [];
                        for (const p of raw) {
                          const pid = (p && (p.id || p.regie_id || p.project_id)) || "";
                          if (!pid || seen.has(pid)) continue;
                          seen.add(pid);
                          clean.push({ id: String(pid), label: p.label || "—", is_regie: !!p.is_regie });
                        }
                        setProjectsInfo(clean);
                        if (clean.length > 0) toast.success(`✅ Alocări găsite: ${clean.length}`);
                        else toast.info("Încă nu ești alocat pe această zi");
                      }).catch(() => toast.error("Eroare la reîncărcare"));
                    }}
                    className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-orange-400/20 hover:bg-orange-400/30 border border-orange-400/40 text-orange-100"
                    data-testid="emp-hours-refresh-allocation"
                  >
                    🔄 Reîncarcă alocările
                  </button>
                  <div className="mt-3 pt-2 border-t border-orange-400/30">
                    <div className="text-[11px] font-bold text-orange-100 mb-1">Ai lucrat totuși? Alege șantierul / regia (orele tale vor fi marcate pentru verificare de către echipă):</div>
                    <select
                      value={selfDeclaredSite}
                      onFocus={() => {
                        if (!selfSites) {
                          api.get(`/me/active-sites?day=${date}`)
                            .then((r) => setSelfSites(r.data || { projects: [], regii: [] }))
                            .catch(() => setSelfSites({ projects: [], regii: [] }));
                        }
                      }}
                      onChange={(e) => setSelfDeclaredSite(e.target.value)}
                      className="w-full bg-white/10 border border-orange-400/40 text-white rounded-md px-2 py-2 text-sm"
                      data-testid="self-declared-site-select"
                    >
                      <option value="" className="text-black">— alege șantierul / regia —</option>
                      {(selfSites?.projects || []).map((p) => <option className="text-black" key={p.id} value={p.id}>🏗 {p.name}</option>)}
                      {(selfSites?.regii || []).map((rg) => <option className="text-black" key={rg.id} value={rg.id}>⚙ {rg.name}</option>)}
                    </select>
                    {selfDeclaredSite && (
                      <div className="text-[10px] text-orange-200/80 mt-1" data-testid="self-declared-hint">
                        ⚠ Orele tale vor apărea cu marcaj „adăugat singur" — echipa de pe șantier confirmă dacă e adevărat.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Banner informativ — orele tale au fost deja completate (NU mai blochează submit-ul) */}
        {myHoursLogged && myHoursLogged.exists && !myHoursLogged.by_self && (
          <div
            className="border border-amber-400/40 bg-amber-500/10 rounded-md p-3 text-white"
            data-testid="emp-hours-already-logged-banner"
          >
            <div className="flex items-start gap-2">
              <span className="text-xl leading-none">ℹ️</span>
              <div className="flex-1">
                <div className="font-bold text-amber-200 text-sm uppercase tracking-wider mb-1">
                  Orele tale au fost completate deja
                </div>
                <div className="text-sm leading-relaxed">
                  Pe data de <span className="font-mono font-bold">{date}</span> ai deja{" "}
                  <span className="font-bold">{Number(myHoursLogged.hours || 0).toFixed(1)}h</span> lucrate
                  {myHoursLogged.hours_driven > 0 && (
                    <> + <span className="font-bold">{Number(myHoursLogged.hours_driven).toFixed(1)}h</span> conduse</>
                  )}
                  {myHoursLogged.submitted_by_name && (
                    <> înregistrate de <span className="font-bold text-amber-200">{myHoursLogged.submitted_by_name}</span></>
                  )}
                  {myHoursLogged.project_label && (
                    <> pe <span className="italic">{myHoursLogged.project_label}</span></>
                  )}
                  . Dacă salvezi din nou, valoarea va fi suprascrisă.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabel ore per persoană */}
        <div className="border border-white/15 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr className="text-left text-[10px] uppercase tracking-wider text-white/60">
                <th className="px-2 py-1.5">Persoană</th>
                <th className="px-2 py-1.5 text-right w-[90px]">Ore</th>
                <th className="px-2 py-1.5 text-right w-[110px]">Ore conduse</th>
                <th className="px-2 py-1.5 text-center w-[80px]">Absent</th>
                <th className="px-2 py-1.5 w-[40px]"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const driverTitle = r.is_driver
                  ? `Șofer planificat${r.driven_cars && r.driven_cars.length ? ` · ${r.driven_cars.join(", ")}` : ""}`
                  : "";
                const already = !r.is_self ? personsHoursStatus[r.person_id] : null;
                const alreadyExists = !!(already && already.exists);
                const alreadyByName = already?.submitted_by_name || (already?.by_self ? "el/ea" : null);
                const alreadyH = Number(already?.hours || 0);
                const alreadyDrv = Number(already?.hours_driven || 0);
                const alreadyTotalH = alreadyH + alreadyDrv;
                const isLocked = alreadyExists && !r.override;
                return (
                  <React.Fragment key={r.person_id}>
                  <tr className={`border-t border-white/10 ${r.absent ? "opacity-50" : ""} ${r.is_driver && !r.absent ? "bg-amber-500/10" : ""} ${alreadyExists ? "bg-orange-500/10" : ""}`} data-testid={`emp-hours-row-${r.person_id}`}>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {r.is_driver && (
                          <span title={driverTitle} className="text-base leading-none" data-testid={`emp-hours-row-${r.person_id}-driver-icon`}>🔑</span>
                        )}
                        <span className={`font-semibold ${r.is_self ? "text-amber-300" : ""} ${r.is_driver ? "text-amber-200" : ""}`}>{r.name}</span>
                        {r.is_self && <span className="text-[9px] px-1 rounded bg-amber-500/30 text-amber-200 font-bold uppercase">tu</span>}
                        {r.is_driver && (
                          <span title={driverTitle} className="text-[9px] px-1 rounded bg-amber-400/30 text-amber-200 font-bold uppercase border border-amber-400/50">
                            șofer
                          </span>
                        )}
                        {r.ad_hoc && <span className="text-[9px] px-1 rounded bg-purple-500/30 text-purple-200 font-bold uppercase">ad-hoc</span>}
                      </div>
                      {r.is_driver && r.driven_cars && r.driven_cars.length > 0 && (
                        <div className="text-[10px] text-amber-300/80 mt-0.5 font-mono truncate" title={driverTitle}>
                          {r.driven_cars.join(" · ")}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <Input type="number" step="0.5" min={0} max={24} className={`h-7 text-right text-sm tabular-nums bg-white/10 text-white disabled:opacity-40 ${!r.absent && !isLocked && String(r.hours ?? "").trim() === "" ? "border-red-400/70 ring-1 ring-red-400/40 placeholder:text-red-200/70" : "border-white/20"}`} disabled={r.absent || isLocked} value={r.absent ? "0" : r.hours} placeholder={r.absent ? "" : "obligatoriu"} onChange={(e) => updateRow(r.person_id, { hours: e.target.value })} data-testid={`emp-hours-row-${r.person_id}-hours`}/>
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <Input type="number" step="0.5" min={0} max={24} className={`h-7 text-right text-sm tabular-nums bg-white/10 text-white disabled:opacity-40 ${r.is_driver ? "border-amber-400/70 ring-1 ring-amber-400/40 placeholder:text-amber-200/60" : "border-white/20"}`} disabled={r.absent || isLocked} value={r.absent ? "0" : r.hours_driven} placeholder={r.is_driver ? "ore?" : "0"} onChange={(e) => updateRow(r.person_id, { hours_driven: e.target.value })} data-testid={`emp-hours-row-${r.person_id}-driven`}/>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <input type="checkbox" checked={!!r.absent} disabled={isLocked} onChange={(e) => updateRow(r.person_id, { absent: e.target.checked, hours: e.target.checked ? "0" : "", hours_driven: e.target.checked ? "0" : r.hours_driven })} data-testid={`emp-hours-row-${r.person_id}-absent`}/>
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {!r.is_self && (
                        <button type="button" onClick={() => removeRow(r.person_id)} className="text-red-400 hover:text-red-300" title="Scoate din tabel">
                          <X size={14}/>
                        </button>
                      )}
                    </td>
                  </tr>
                  {alreadyExists && (
                    <tr className="border-t border-orange-400/30 bg-orange-500/[0.06]" data-testid={`emp-hours-row-${r.person_id}-already-info`}>
                      <td colSpan={5} className="px-3 py-2">
                        <div className="flex items-start gap-2 flex-wrap">
                          <span className="text-base leading-none mt-0.5">🚫</span>
                          <div className="flex-1 min-w-0 text-[12px] leading-relaxed text-orange-100">
                            <span className="font-bold text-orange-200 uppercase text-[10px] tracking-wider mr-1">Deja înregistrat</span>
                            <span className="font-bold text-white">{alreadyH.toFixed(1)}h lucrate</span>
                            {alreadyDrv > 0 && <> + <span className="font-bold text-white">{alreadyDrv.toFixed(1)}h conduse</span></>}
                            {alreadyByName && <> de <span className="font-bold text-amber-200">{alreadyByName}</span></>}
                            {already?.project_label && <> pe <span className="italic text-orange-100/90">{already.project_label}</span></>}
                            .
                            <span className="ml-1 text-orange-200/80">Nu completa din nou — datele sunt deja salvate.</span>
                          </div>
                          <label className="inline-flex items-center gap-1.5 text-[11px] text-orange-200 cursor-pointer select-none whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={!!r.override}
                              onChange={(e) => {
                                const on = e.target.checked;
                                updateRow(r.person_id, {
                                  override: on,
                                  hours: on ? String(alreadyH) : r.hours,
                                  hours_driven: on ? String(alreadyDrv) : r.hours_driven,
                                });
                              }}
                              data-testid={`emp-hours-row-${r.person_id}-override`}
                            />
                            Suprascrie (vreau să corectez)
                          </label>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Adaugă ad-hoc — doar pentru conturile cu team mode activat */}
        {teamMode && (
          <div className="border-t border-white/10 pt-3" data-testid="emp-hours-team-section">
            <Label className="small-label">➕ A venit cineva neplanificat?</Label>
            <Select
              value="__pick__"
              onValueChange={(v) => {
                if (!v || v === "__pick__") return;
                const p = activePersons.find((x) => x.id === v);
                if (p) addAdHoc(p);
              }}
            >
              <SelectTrigger className="mt-1 bg-white/10 border-white/20 text-white" data-testid="emp-hours-adhoc-select">
                <SelectValue placeholder="Alege un coleg din listă…"/>
              </SelectTrigger>
              <SelectContent className="max-h-[280px]">
                <SelectItem value="__pick__" disabled>— Alege un coleg —</SelectItem>
                {availableAdHocPersons.length === 0 && (
                  <SelectItem value="__empty__" disabled>Toți colegii sunt deja în tabel</SelectItem>
                )}
                {availableAdHocPersons.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`emp-hours-adhoc-opt-${p.id}`}>
                    {p.name}
                    {siteHint(p.id, siteLogged) && <span className="text-amber-600 font-semibold">{siteHint(p.id, siteLogged)}</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-white/40 mt-1">Persoanele deja în tabel nu apar în listă.</div>
          </div>
        )}

        {availableCars.length > 0 && (
          <div>
            <Label className="small-label">Cu ce mașină ai condus tu? (opțional)</Label>
            <Select value={carId || "__none__"} onValueChange={(v) => setCarId(v === "__none__" ? "" : v)}>
              <SelectTrigger className="mt-1" data-testid="emp-hours-car"><SelectValue placeholder="— Selectează —"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Niciuna —</SelectItem>
                {availableCars.map((car) => (
                  <SelectItem key={car.id} value={car.id}>{car.display || car.numar_inmatriculare || "?"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {errorMsg && (
          <div className="px-3 py-2 rounded border border-red-500 bg-red-900/30 text-red-200 text-sm font-semibold" data-testid="emp-hours-error">
            {errorMsg}
          </div>
        )}

        {/* Ai lucrat și pe alt șantier? */}
        <div className="border border-white/15 bg-white/[0.03] rounded-md p-3" data-testid="extra-site-section">
          <button
            type="button"
            onClick={async () => {
              const v = !extraOpen;
              setExtraOpen(v);
              if (v && !extraSites) {
                try {
                  const r = await api.get(`/me/active-sites?day=${date}`);
                  setExtraSites(r.data || { projects: [], regii: [] });
                } catch { setExtraSites({ projects: [], regii: [] }); }
              }
            }}
            className="w-full text-left text-sm font-bold text-white flex items-center gap-2"
            data-testid="extra-site-toggle"
          >
            🏗 Ai lucrat și pe ALT șantier în această zi?
            <span className="ml-auto text-white/60">{extraOpen ? "▲" : "▼ da, adaugă"}</span>
          </button>
          {extraOpen && (
            <div className="mt-3 space-y-2">
              <select
                value={extraSite}
                onChange={(e) => setExtraSite(e.target.value)}
                className="w-full bg-white/10 border border-white/20 text-white rounded-md px-2 py-2 text-sm"
                data-testid="extra-site-select"
              >
                <option value="" className="text-black">— alege șantierul / regia —</option>
                {(extraSites?.projects || []).map((p) => <option className="text-black" key={p.id} value={p.id}>🏗 {p.name}</option>)}
                {(extraSites?.regii || []).map((rg) => <option className="text-black" key={rg.id} value={rg.id}>⚙ {rg.name}</option>)}
              </select>
              {renderSiteLogged(extraLogged, "extra-site-logged-panel", extraSite && extraSites ? ((extraSites.regii || []).some((r) => r.id === extraSite) ? { regie_id: extraSite } : { project_id: extraSite }) : null, setExtraLogged)}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="small-label text-white/70">Ore lucrate acolo</Label>
                  <Input type="number" step="0.5" min="0" value={extraHours} onChange={(e) => setExtraHours(e.target.value)} className="mt-1 bg-white/10 border-white/20 text-white" data-testid="extra-site-hours" />
                </div>
                <div>
                  <Label className="small-label text-white/70">🚗 Ore conduse</Label>
                  <Input type="number" step="0.5" min="0" value={extraDriven} onChange={(e) => setExtraDriven(e.target.value)} className="mt-1 bg-white/10 border-white/20 text-white" data-testid="extra-site-driven" />
                </div>
              </div>
              <div>
                <Label className="small-label text-white/70">Cu cine ai lucrat acolo? (opțional)</Label>
                <select
                  value=""
                  onChange={(e) => {
                    const id = e.target.value;
                    if (!id) return;
                    const p = activePersons.find((x) => x.id === id);
                    if (p && !extraCoworkers.some((c) => c.person_id === id)) {
                      setExtraCoworkers((arr) => [...arr, { person_id: id, name: p.name, hours: extraHours || "" }]);
                    }
                  }}
                  className="w-full bg-white/10 border border-white/20 text-white rounded-md px-2 py-2 text-sm mt-1"
                  data-testid="extra-site-coworker-select"
                >
                  <option value="" className="text-black">+ adaugă coleg…</option>
                  {activePersons
                    .filter((p) => p.id !== myId && !extraCoworkers.some((c) => c.person_id === p.id))
                    .map((p) => <option className="text-black" key={p.id} value={p.id}>{p.name}{siteHint(p.id, extraLogged)}</option>)}
                </select>
                {extraCoworkers.map((c) => (
                  <div key={c.person_id} className="flex items-center gap-2 mt-1.5" data-testid={`extra-coworker-${c.person_id}`}>
                    <span className="text-xs text-white/80 flex-1 truncate">
                      {c.name}
                      {siteHint(c.person_id, extraLogged) && <span className="text-amber-300 font-semibold">{siteHint(c.person_id, extraLogged)}</span>}
                    </span>
                    <Input type="number" step="0.5" min="0" placeholder="ore" value={c.hours} onChange={(e) => setExtraCoworkers((arr) => arr.map((x) => x.person_id === c.person_id ? { ...x, hours: e.target.value } : x))} className="w-20 h-8 bg-white/10 border-white/20 text-white text-sm" />
                    <button type="button" onClick={() => setExtraCoworkers((arr) => arr.filter((x) => x.person_id !== c.person_id))} className="text-red-300 text-xs font-bold px-1">✕</button>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-white/40">Orele de aici se salvează separat pe șantierul ales, la apăsarea butonului de salvare. Zilele cu 2 șantiere sunt confirmate de admin.</div>
            </div>
          )}
        </div>

        {/* Upload Raport ore semnat — obligatoriu cel târziu vineri seara la regii */}
        {(() => {
          const selNow = projectsInfo.find((x) => x.id === project);
          const dowNow = new Date(date + "T00:00:00").getDay(); // 5 = vineri, 6 = sâmbătă
          const fridayPulse = !!selNow?.is_regie && (dowNow === 5 || dowNow === 6) && !reportFile;
          return (
        <div
          className={`border rounded-md p-3 transition-all ${fridayPulse ? "border-amber-400 bg-amber-500/10 animate-pulse ring-2 ring-amber-400/50" : "border-white/15 bg-white/[0.03]"}`}
          data-testid="emp-hours-report-section"
        >
          <div className="flex items-center gap-2 mb-2">
            <FileSignature size={16} className={fridayPulse ? "text-amber-300" : "text-emerald-300"}/>
            <span className="text-sm font-bold text-white">Raport ore semnat de Vorarbeiter</span>
          </div>
          {fridayPulse ? (
            <div className="text-[11px] text-amber-300 font-bold mb-2 leading-snug" data-testid="emp-report-friday-warning">
              ⚠ {dowNow === 5 ? "Azi e VINERI" : "E SÂMBĂTĂ"} — nu uita raportul ore semnat! Cel târziu vineri seara trebuie încărcat.
            </div>
          ) : (
            <div className="text-[11px] text-white/50 mb-2 leading-snug">
              Poză sau PDF cu raportul semnat. Poți încărca unul săptămânal sau mai multe zilnice — <strong className="text-white/70">cel târziu vineri seara</strong>.
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 mb-2">
            <button
              type="button"
              onClick={() => setReportType("tages")}
              className={`px-2.5 py-2 rounded text-left border transition-all ${reportType === "tages" ? "bg-emerald-500/20 border-emerald-400 ring-1 ring-emerald-400/40" : "border-white/15 hover:border-white/30"}`}
              data-testid="emp-hours-report-type-tages"
            >
              <div className="text-xs font-bold text-white">📄 Raport ore — zilnic</div>
              <div className="text-[10px] text-white/50">O singură zi ({date})</div>
            </button>
            <button
              type="button"
              onClick={() => setReportType("wochen")}
              className={`px-2.5 py-2 rounded text-left border transition-all ${reportType === "wochen" ? "bg-emerald-500/20 border-emerald-400 ring-1 ring-emerald-400/40" : "border-white/15 hover:border-white/30"}`}
              data-testid="emp-hours-report-type-wochen"
            >
              <div className="text-xs font-bold text-white">📅 Raport ore — săptămânal</div>
              <div className="text-[10px] text-white/50">Toată săptămâna (interval)</div>
            </button>
          </div>
          {reportType === "wochen" && (
            <div className="mb-2">
              <Label className="small-label text-white/60">Săptămâna se termină pe (duminică)</Label>
              <Input
                type="date"
                value={reportEndDate}
                min={date}
                onChange={(e) => setReportEndDate(e.target.value)}
                className="mt-1 bg-white/10 border-white/20 text-white"
                data-testid="emp-hours-report-end-date"
              />
            </div>
          )}
          <DropZone onFiles={(files) => { setReportFile(files[0] || null); }} className="grid grid-cols-2 gap-2 rounded" testId="emp-report-dropzone">
            <label className={`flex items-center justify-center gap-2 px-2.5 py-2.5 rounded border border-dashed cursor-pointer transition-all ${fridayPulse ? "border-amber-400/70 bg-amber-500/10 hover:bg-amber-500/20" : "border-white/25 bg-white/5 hover:bg-white/10"}`}>
              <span className="text-[11px] text-white/80 font-bold">📷 Fă poză</span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => setReportFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="emp-hours-report-camera-input"
              />
            </label>
            <label className={`flex items-center justify-center gap-2 px-2.5 py-2.5 rounded border border-dashed cursor-pointer transition-all ${fridayPulse ? "border-amber-400/70 bg-amber-500/10 hover:bg-amber-500/20" : "border-white/25 bg-white/5 hover:bg-white/10"}`}>
              <span className="text-[11px] text-white/80 font-bold">🖼 Din galerie / PDF</span>
              <input
                type="file"
                accept=".pdf,image/png,image/jpeg,image/jpg,image/webp,image/heic,image/heif"
                onChange={(e) => setReportFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="emp-hours-report-file-input"
              />
            </label>
          </DropZone>
          {reportFile && (
            <div className="flex items-center justify-between gap-2 mt-2 px-2.5 py-1.5 rounded bg-emerald-500/15 border border-emerald-400/40">
              <span className="text-[11px] text-emerald-200 truncate" data-testid="emp-hours-report-filename">✓ {reportFile.name}</span>
              <button
                type="button"
                onClick={() => setReportFile(null)}
                className="text-red-300 hover:text-red-200 text-[10px] font-bold uppercase tracking-wider flex-shrink-0"
                data-testid="emp-hours-report-clear"
              >
                Șterge
              </button>
            </div>
          )}
        </div>
          );
        })()}
      </div>
      <DialogFooter className="mt-6 gap-2">
        <button type="button" onClick={onClose} className="sb-outline-btn" disabled={busy}>Anulează</button>
        <button
          type="button"
          onClick={submit}
          className="sb-primary-btn"
          disabled={busy}
          data-testid="emp-hours-submit"
        >
          {busy ? "Se salvează…" : (teamMode ? "Salvează echipa →" : "Salvează →")}
        </button>
      </DialogFooter>
    </ModalShell>
  );
}

export function FuelModal({ onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [todayCars, setTodayCars] = useState([]);
  const [allCars, setAllCars] = useState([]);
  const [carId, setCarId] = useState("");
  const [date, setDate] = useState(today);
  const [liters, setLiters] = useState("");
  const [km, setKm] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("chip");
  const [chipName, setChipName] = useState("");
  const [knownChips, setKnownChips] = useState([]); // distinct chip names previously used
  const [loadingCars, setLoadingCars] = useState(true);

  useEffect(() => {
    // Cererile sunt independente — lista de mașini NU trebuie să aștepte /me/today-cars
    api.get("/cars")
      .then((all) => setAllCars(all.data || []))
      .catch(() => {})
      .finally(() => setLoadingCars(false));
    api.get("/me/today-cars")
      .then((t) => {
        setTodayCars(t.data.cars || []);
        if (t.data.cars?.length === 1) setCarId(t.data.cars[0].id);
      })
      .catch(() => {});
    api.get("/fuel-chips")
      .then((chips) => {
        const raw = Array.isArray(chips?.data) ? chips.data : (chips?.data?.chips || []);
        const names = Array.from(new Set(raw.map((c) => c.chip_no || c._id).filter(Boolean))).sort();
        setKnownChips(names);
      })
      .catch(() => {});
  }, []);

  const submit = async () => {
    if (!carId) return toast.error("Selectează autoturismul");
    if (!liters || parseFloat(liters) <= 0) return toast.error("Specifică litrii");
    try {
      await api.post(`/cars/${carId}/fuel`, {
        data_alimentare: date,
        litri: parseFloat(liters),
        numar_km: parseFloat(km) || 0,
        numar_chip: paymentMethod === "chip" ? (chipName || null) : null,
        // metadata extra (backend ignoră dar mongo le va păstra în alt flow dacă va fi nevoie)
        payment_method: paymentMethod,
      });
      toast.success("Alimentare înregistrată");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  // Sort cars: today's first, then rest.
  // Mașinile cu defect minor (drivable) rămân în listă — tot trebuie alimentate.
  const sortedCars = useMemo(() => {
    const todayIds = new Set(todayCars.map((c) => c.id));
    const rest = allCars.filter((c) => !todayIds.has(c.id) && !(c.status === "defect" && c.defect_drivable === false));
    return [...todayCars, ...rest];
  }, [todayCars, allCars]);

  return (
    <ModalShell title="⛽ Alimentează" onClose={onClose} maxW="max-w-lg" swissBlack>
      <div className="space-y-4">
        <div>
          <Label className="small-label">Autoturism {todayCars.length > 0 && <span className="text-[10px] text-red-500 font-bold ml-1">★ alocate azi</span>}</Label>
          <Select value={carId} onValueChange={setCarId}>
            <SelectTrigger className="mt-1" data-testid="emp-fuel-car"><SelectValue placeholder="Selectează"/></SelectTrigger>
            <SelectContent>
              {loadingCars && <div className="text-xs text-zinc-400 italic px-2 py-2" data-testid="emp-fuel-loading">Se încarcă mașinile...</div>}
              {!loadingCars && allCars.length === 0 && <div className="text-xs text-red-500 px-2 py-2" data-testid="emp-fuel-no-cars">Nu s-au putut încărca mașinile — reîncearcă.</div>}
              {todayCars.length > 0 && <div className="text-[10px] text-red-500 px-2 pt-1 uppercase font-bold tracking-wider">Alocate azi</div>}
              {todayCars.map((c) => <SelectItem key={c.id} value={c.id}>★ {c.display}</SelectItem>)}
              {todayCars.length > 0 && <div className="text-[10px] text-zinc-400 px-2 pt-1 uppercase font-bold tracking-wider">Toate</div>}
              {sortedCars.filter((c) => !todayCars.find((t) => t.id === c.id)).map((c) => {
                const intern = c.intern_number;
                const plate = c.numar_inmatriculare;
                let label;
                if (intern && plate) label = `#${intern} · ${plate}`;
                else if (intern) label = `#${intern}`;
                else if (plate) label = plate;
                else label = `${c.marca || ""} ${c.model || ""}`.trim() || c.id.slice(0, 6);
                if ((intern || plate) && (c.marca || c.model)) {
                  label += ` — ${[c.marca, c.model].filter(Boolean).join(" ")}`;
                }
                if (c.status === "defect") label += " ⚠";
                return (<SelectItem key={c.id} value={c.id}>{label}</SelectItem>);
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="small-label">Data</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1"/>
          </div>
          <div>
            <Label className="small-label">Kilometri actuali</Label>
            <Input type="number" value={km} onChange={(e) => setKm(e.target.value)} placeholder="ex: 145000" className="mt-1" data-testid="emp-fuel-km"/>
          </div>
        </div>
        <div>
          <Label className="small-label">Litri</Label>
          <Input type="number" step="0.01" value={liters} onChange={(e) => setLiters(e.target.value)} placeholder="ex: 45.5" className="mt-1" data-testid="emp-fuel-liters"/>
        </div>
        <div>
          <Label className="small-label">Metoda de plată</Label>
          <div className="flex gap-2 mt-2">
            {[
              { v: "chip", l: "💳 Chip" },
              { v: "cash", l: "💵 Cash firmă" },
              { v: "cash_personal", l: "👛 Cash personal" },
            ].map((m) => (
              <button key={m.v} type="button" onClick={() => setPaymentMethod(m.v)}
                className={`flex-1 py-2.5 text-xs font-semibold border transition-colors ${paymentMethod === m.v ? "bg-red-600 text-white border-red-600" : "bg-transparent text-white/70 border-white/20 hover:border-white/50"}`}
                data-testid={`emp-fuel-pay-${m.v}`}>{m.l}</button>
            ))}
          </div>
        </div>
        {paymentMethod === "chip" && (
          <div>
            <Label className="small-label">Numele chip-ului</Label>
            <div className="relative mt-1">
              <Input
                value={chipName}
                onChange={(e) => setChipName(e.target.value)}
                placeholder={knownChips.length > 0 ? `Selectează sau scrie unul nou (${knownChips.length} salvate)` : "ex: BP, Migros"}
                list="known-chips-datalist"
                data-testid="emp-fuel-chip"
                autoComplete="off"
              />
              <datalist id="known-chips-datalist">
                {knownChips.map((n) => <option key={n} value={n}/>)}
              </datalist>
            </div>
            {knownChips.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1" data-testid="emp-fuel-chip-suggestions">
                {knownChips.slice(0, 8).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setChipName(n)}
                    className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${chipName === n ? "bg-amber-600 text-white border-amber-600" : "bg-white text-zinc-700 border-zinc-300 hover:bg-amber-50 hover:border-amber-300"}`}
                    title={`Folosește chip-ul ${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            )}
            <div className="text-[10px] text-zinc-400 mt-1">
              💡 Chip-urile noi sunt salvate automat după prima înregistrare.
            </div>
          </div>
        )}
      </div>
      <DialogFooter className="mt-6 gap-2">
        <button type="button" onClick={onClose} className="sb-outline-btn">Anulează</button>
        <button type="button" onClick={submit} className="sb-primary-btn" data-testid="emp-fuel-submit">Înregistrează →</button>
      </DialogFooter>
    </ModalShell>
  );
}

function VacationModal({ onClose }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const submit = async () => {
    if (!start || !end) return toast.error("Specifică intervalul");
    try {
      await api.post("/vacations", { start_date: start, end_date: end, reason });
      toast.success("Cerere trimisă spre aprobare");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };
  return (
    <ModalShell title="🏖️ Cerere concediu" onClose={onClose} maxW="max-w-md">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="small-label">De la</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1" data-testid="emp-vac-start"/></div>
          <div><Label className="small-label">Până la</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1" data-testid="emp-vac-end"/></div>
        </div>
        <div>
          <Label className="small-label">Motiv (opțional)</Label>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" placeholder="ex: vacanță familie"/>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Anulează</Button>
        <Button onClick={submit} className="bg-cyan-600 hover:bg-cyan-700" data-testid="emp-vac-submit">Trimite cerere</Button>
      </DialogFooter>
    </ModalShell>
  );
}

function StatsModal({ onClose }) {
  const today = new Date();
  const curMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const prevD = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const prevMonth = `${prevD.getFullYear()}-${String(prevD.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(curMonth);
  const monthLabel = (m) => {
    const [y, mm] = m.split("-");
    return new Date(Number(y), Number(mm) - 1, 1).toLocaleDateString("ro-RO", { month: "long", year: "numeric" });
  };
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [reportsByDay, setReportsByDay] = useState({}); // { "YYYY-MM-DD": [report,...] }
  useEffect(() => {
    if (!user?.person_id) return;
    setStats(null);
    api.get(`/persons/${user.person_id}/calendar?month=${month}`).then((r) => {
      const entries = r.data || [];
      const totalH = entries.reduce((s, e) => s + (e.hours || 0), 0);
      const totalDriven = entries.reduce((s, e) => s + (e.hours_driven || 0), 0);
      // Zile lucrate = zile UNICE cu ore > 0 (multi-alocările pe aceeași zi numără 1 dată)
      const workedDays = new Set(entries.filter((e) => (e.hours || 0) > 0 || (e.hours_driven || 0) > 0).map((e) => e.date));
      const days = workedDays.size;
      // Salariu = sum((hours + hours_driven) * hourly_rate) per entry
      const salary = entries.reduce((s, e) => s + (((e.hours || 0) + (e.hours_driven || 0)) * (e.hourly_rate || 0)), 0);
      // Diurnă = sum of diurna field (deja 16 CHF doar pe zile cu ore > 0, o singură dată / zi)
      const diurna = entries.reduce((s, e) => s + (e.diurna || 0), 0);
      const total = entries.reduce((s, e) => s + (e.total || 0), 0);
      // Group per zi pentru breakdown
      const byDay = {};
      for (const e of entries) {
        const d = e.date;
        if (!byDay[d]) byDay[d] = { date: d, hours: 0, driven: 0, rate: 0, diurna: 0, total: 0, projects: [], submitters: new Map() };
        byDay[d].hours += (e.hours || 0);
        byDay[d].driven += (e.hours_driven || 0);
        byDay[d].rate = e.hourly_rate || byDay[d].rate;
        byDay[d].diurna += (e.diurna || 0);
        byDay[d].total += (e.total || 0);
        if (e.project_label) byDay[d].projects.push(e.project_label);
        // Audit: cine a introdus orele pe această zi + când. Putem avea
        // mai multe intrări pentru aceeași zi (multi-alocare) — colectăm unic.
        const submitterName = e.created_by_name || e.logged_by_user_id;
        const submitterAt = e.created_at || e.logged_at;
        if (submitterName) {
          const key = String(submitterName);
          const cur = byDay[d].submitters.get(key);
          if (!cur || (submitterAt && submitterAt > (cur || ""))) {
            byDay[d].submitters.set(key, submitterAt || "");
          }
        }
      }
      const dayRows = Object.values(byDay)
        .map((d) => ({
          ...d,
          // Convertim Map → array pentru render: [{name, at}, ...]
          submitters: Array.from(d.submitters.entries()).map(([name, at]) => ({ name, at })),
        }))
        .sort((a, b) => a.date.localeCompare(b.date));
      setStats({ totalH, totalDriven, days, salary, diurna, total, dayRows });
    }).catch(() => setStats({ totalH: 0, totalDriven: 0, days: 0, salary: 0, diurna: 0, total: 0, dayRows: [] }));
    // Încarcă rapoartele semnate pentru luna curentă — îmbogățim breakdown-ul cu un indicator 📎
    api.get(`/signed-reports?month=${month}`).then((r) => {
      const map = {};
      for (const rep of (r.data || [])) {
        // Pentru wochen, marchează fiecare zi din interval
        try {
          const s = new Date(rep.start_date + "T00:00:00");
          const e = new Date((rep.end_date || rep.start_date) + "T00:00:00");
          for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
            const iso = d.toISOString().slice(0, 10);
            if (!map[iso]) map[iso] = [];
            map[iso].push(rep);
          }
        } catch { /* noop */ }
      }
      setReportsByDay(map);
    }).catch(() => setReportsByDay({}));
  }, [user, month]);
  const fmt = (n) => Number(n || 0).toFixed(2);
  const fmtDay = (iso) => {
    try {
      const d = new Date(iso + "T00:00:00");
      return d.toLocaleDateString("ro-RO", { day: "2-digit", month: "short", weekday: "short" });
    } catch { return iso; }
  };
  const showSalary = (user?.role || "").toLowerCase() === "admin";
  return (
    <ModalShell title="📊 Orele mele" onClose={onClose} maxW="max-w-2xl">
      <div className="flex rounded-lg border border-zinc-200 overflow-hidden mb-3 text-sm font-semibold" data-testid="stats-month-toggle">
        <button
          onClick={() => setMonth(curMonth)}
          className={`flex-1 px-3 py-2 transition-colors ${month === curMonth ? "bg-[var(--primary)] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
          data-testid="stats-month-current"
        >
          Luna curentă · {monthLabel(curMonth)}
        </button>
        <button
          onClick={() => setMonth(prevMonth)}
          className={`flex-1 px-3 py-2 transition-colors border-l border-zinc-200 ${month === prevMonth ? "bg-[var(--primary)] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
          data-testid="stats-month-previous"
        >
          Luna trecută · {monthLabel(prevMonth)}
        </button>
      </div>
      {!user?.person_id ? (
        <div className="text-sm text-zinc-500 italic p-4 text-center">Contul tău nu este legat de o persoană din Personal — cere adminului să te asocieze.</div>
      ) : !stats ? <div>Se încarcă…</div> : (
        <div className="space-y-3" data-testid="stats-modal-content" translate="no" lang="ro">
          <div className={`grid grid-cols-1 ${showSalary ? "md:grid-cols-4" : "sm:grid-cols-3"} gap-2`}>
            <div className="card-flat p-3 text-center">
              <div className="small-label" translate="no">Total ore lucrate</div>
              <div className="font-display text-2xl font-extrabold text-[var(--primary)] mt-1" data-testid="stats-total-hours">{stats.totalH.toFixed(1)}</div>
            </div>
            <div className="card-flat p-3 text-center">
              <div className="small-label" translate="no">Ore conduse</div>
              <div className="font-display text-2xl font-extrabold text-amber-600 mt-1">{stats.totalDriven.toFixed(1)}</div>
            </div>
            <div className="card-flat p-3 text-center">
              <div className="small-label" translate="no">Zile lucrate</div>
              <div className="font-display text-2xl font-extrabold text-zinc-900 mt-1" data-testid="stats-worked-days">{stats.days}</div>
            </div>
            {showSalary && (
              <div className="card-flat p-3 text-center bg-green-50 border-green-200">
                <div className="small-label" translate="no">Total CHF</div>
                <div className="font-display text-2xl font-extrabold text-green-700 mt-1" data-testid="stats-total-chf">{fmt(stats.total)}</div>
              </div>
            )}
          </div>
          {showSalary && (
            <div className="card-flat p-3 bg-zinc-50" translate="no">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold mb-2">💰 Calcul</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between" data-testid="stats-salary-row">
                  <span className="text-zinc-600">Salariu (ore × tarif)</span>
                  <span className="font-mono font-semibold">{fmt(stats.salary)} CHF</span>
                </div>
                <div className="flex justify-between" data-testid="stats-diurna-row">
                  <span className="text-zinc-600" title="Diurnă = 16 CHF/zi doar pentru zilele cu ≥ 5h">Diurnă ({stats.days} zile × 16 CHF)</span>
                  <span className="font-mono font-semibold">{fmt(stats.diurna)} CHF</span>
                </div>
                <div className="flex justify-between pt-1.5 mt-1.5 border-t border-zinc-300">
                  <span className="font-bold text-zinc-900">Total de încasat</span>
                  <span className="font-mono font-extrabold text-green-700">{fmt(stats.total)} CHF</span>
                </div>
              </div>
            </div>
          )}
          {stats.dayRows.length > 0 && (
            <div className="card-flat p-0 overflow-hidden">
              <div className="px-3 py-2 bg-zinc-100 border-b border-zinc-200">
                <div className="text-[10px] uppercase tracking-wider text-zinc-600 font-bold" translate="no">📅 Detaliu pe zi</div>
              </div>
              <div className="max-h-[360px] overflow-y-auto" data-testid="stats-day-breakdown">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50 sticky top-0">
                    <tr className="text-[10px] uppercase text-zinc-500">
                      <th className="text-left px-2 py-1.5" translate="no">Data</th>
                      <th className="text-right px-2 py-1.5" translate="no">Ore</th>
                      <th className="text-right px-2 py-1.5" translate="no">Ore conduse</th>
                      {showSalary && <th className="text-right px-2 py-1.5" translate="no">Tarif</th>}
                      {showSalary && <th className="text-right px-2 py-1.5" translate="no">Diurnă</th>}
                      {showSalary && <th className="text-right px-2 py-1.5" translate="no">Total</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {stats.dayRows.map((d) => {
                      const dayReports = reportsByDay[d.date] || [];
                      return (
                      <tr key={d.date} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`stats-day-${d.date}`}>
                        <td className="px-2 py-1.5">
                          <div className="font-medium flex items-center gap-1.5">
                            {fmtDay(d.date)}
                            {dayReports.length > 0 && (
                              <span
                                className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0 rounded bg-emerald-100 text-emerald-700 font-bold uppercase border border-emerald-200"
                                title={dayReports.map((r) => `Raport ore ${r.report_type === "wochen" ? "săptămânal" : "zilnic"} · ${r.regie_name || r.project_name || "—"}`).join(" • ")}
                                data-testid={`stats-day-${d.date}-report-badge`}
                              >
                                📎{dayReports.length > 1 ? `×${dayReports.length}` : ""}
                              </span>
                            )}
                          </div>
                          {d.projects.length > 0 && (
                            <div className="text-[10px] text-zinc-500 truncate max-w-[260px]" title={d.projects.join(", ")}>
                              {[...new Set(d.projects)].join(", ")}
                            </div>
                          )}
                          {d.submitters && d.submitters.length > 0 && (
                            <div className="text-[10px] text-blue-700 mt-0.5 flex items-center gap-1 flex-wrap" data-testid={`stats-day-${d.date}-submitters`}>
                              <span className="text-blue-400">📥</span>
                              <span>Introdus de:</span>
                              {d.submitters.map((s, i) => (
                                <span key={i} className="font-semibold" title={s.at ? `${s.name} · ${new Date(s.at).toLocaleString("ro-RO", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}` : s.name}>
                                  {s.name}{s.at ? <span className="text-blue-500 font-normal"> · {new Date(s.at).toLocaleString("ro-RO", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span> : ""}{i < d.submitters.length - 1 ? "," : ""}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="text-right px-2 py-1.5 font-mono font-semibold">{d.hours.toFixed(1)}</td>
                        <td className="text-right px-2 py-1.5 font-mono text-amber-700">{(d.driven || 0).toFixed(1)}</td>
                        {showSalary && <td className="text-right px-2 py-1.5 font-mono text-zinc-600">{fmt(d.rate)}</td>}
                        {showSalary && <td className="text-right px-2 py-1.5 font-mono text-amber-700">{fmt(d.diurna)}</td>}
                        {showSalary && <td className="text-right px-2 py-1.5 font-mono font-bold text-green-700">{fmt(d.total)}</td>}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {stats.dayRows.length === 0 && (
            <div className="text-center text-sm text-zinc-400 italic py-6">Nicio oră înregistrată în luna curentă.</div>
          )}
        </div>
      )}
    </ModalShell>
  );
}

function ReportsModal({ onClose }) {
  const { user } = useAuth();
  const today = new Date();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [month, setMonth] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setItems(null);
    api.get(`/signed-reports?month=${month}`).then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [month]);

  const onDelete = async (rid) => {
    if (!window.confirm("Sigur ștergi acest raport?")) return;
    setBusy(true);
    try {
      await api.delete(`/signed-reports/${rid}`);
      toast.success("Raport șters");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergere");
    } finally { setBusy(false); }
  };

  const fmtDay = (iso) => {
    try { return new Date(iso + "T00:00:00").toLocaleDateString("ro-RO", { day: "2-digit", month: "short" }); }
    catch { return iso; }
  };

  const buildFileUrl = (rid, download = false) => {
    const token = tokenStore.get() || "";
    const qs = new URLSearchParams({ token });
    if (download) qs.set("download", "1");
    return `${API_BASE}/signed-reports/${rid}/file?${qs.toString()}`;
  };

  return (
    <ModalShell title="📎 Rapoarte ore semnate" onClose={onClose} maxW="max-w-3xl">
      <div className="space-y-3" data-testid="reports-modal-content">
        <div className="flex items-center gap-2">
          <Label className="small-label">Luna</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-[180px]" data-testid="reports-month-input"/>
        </div>
        {!items && <div className="text-sm text-zinc-500 italic py-6 text-center">Se încarcă…</div>}
        {items && items.length === 0 && (
          <div className="text-center text-sm text-zinc-400 italic py-8" data-testid="reports-empty">
            Niciun raport încărcat în această lună.
          </div>
        )}
        {items && items.length > 0 && (
          <div className="card-flat p-0 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-zinc-100 border-b border-zinc-200">
                <tr className="text-[10px] uppercase text-zinc-600">
                  <th className="text-left px-2 py-1.5">Tip</th>
                  <th className="text-left px-2 py-1.5">Perioadă</th>
                  {isAdmin && <th className="text-left px-2 py-1.5">Persoană</th>}
                  <th className="text-left px-2 py-1.5">Activitate</th>
                  <th className="text-left px-2 py-1.5">Notă</th>
                  <th className="text-left px-2 py-1.5">Încărcat</th>
                  <th className="text-right px-2 py-1.5 w-[140px]">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const dateRange = r.report_type === "wochen" && r.end_date && r.end_date !== r.start_date
                    ? `${fmtDay(r.start_date)} → ${fmtDay(r.end_date)}`
                    : fmtDay(r.start_date);
                  return (
                    <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`report-row-${r.id}`}>
                      <td className="px-2 py-1.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${r.report_type === "wochen" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                          {r.report_type === "wochen" ? "Săptămânal" : "Zilnic"}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 font-mono whitespace-nowrap">{dateRange}</td>
                      {isAdmin && <td className="px-2 py-1.5">{r.person_name || "—"}</td>}
                      <td className="px-2 py-1.5 text-zinc-700">{r.regie_name || r.project_name || <span className="text-zinc-400">—</span>}</td>
                      <td className="px-2 py-1.5 text-zinc-500 max-w-[160px] truncate" title={r.note || ""}>{r.note || "—"}</td>
                      <td className="px-2 py-1.5 text-zinc-500 text-[11px] whitespace-nowrap">{r.uploaded_by_name || "—"}</td>
                      <td className="px-2 py-1.5 text-right">
                        <div className="flex justify-end gap-1">
                          <a
                            href={buildFileUrl(r.id)}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-zinc-100 hover:bg-zinc-200 border border-zinc-300 text-zinc-700"
                            data-testid={`report-view-${r.id}`}
                          >
                            <FileText size={11}/>Vezi
                          </a>
                          <a
                            href={buildFileUrl(r.id, true)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700"
                            data-testid={`report-download-${r.id}`}
                          >
                            <Download size={11}/>
                          </a>
                          <button
                            type="button"
                            onClick={() => onDelete(r.id)}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-red-50 hover:bg-red-100 border border-red-200 text-red-700"
                            data-testid={`report-delete-${r.id}`}
                            title="Șterge raport"
                          >
                            <Trash size={11}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ModalShell>
  );
}


function BagsModal({ onClose }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [fest, setFest] = useState("");
  const [schwach, setSchwach] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/me/active-projects").then((r) => {
      setProjects(r.data.projects || []);
      if ((r.data.projects || []).length === 1) setProjectId(r.data.projects[0].id);
    }).catch(() => {});
  }, []);

  const totalBags = (parseInt(fest) || 0) + (parseInt(schwach) || 0);
  const totalKg = totalBags * 14;

  const submit = async () => {
    if (!projectId) return toast.error("Selectează proiectul");
    if (totalBags <= 0) return toast.error("Specifică numărul de saci");
    setSubmitting(true);
    try {
      await api.post(`/projects/${projectId}/bags-add`, {
        festgebunden: parseInt(fest) || 0,
        schwachgebunden: parseInt(schwach) || 0,
        note: note || null,
      });
      toast.success(`${totalBags} saci înregistrați (~${totalKg} kg)`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setSubmitting(false); }
  };

  return (
    <ModalShell title="🗑️ Aruncă saci" onClose={onClose} maxW="max-w-lg">
      <div className="space-y-3">
        {projects.length === 0 ? (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
            Nu ești atribuit la niciun proiect activ. Cere planificatorului să te aloce.
          </div>
        ) : (
          <>
            <div>
              <Label className="small-label">Șantier (proiect)</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger className="mt-1" data-testid="emp-bags-project"><SelectValue placeholder="Alege proiectul"/></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}{p.adresa ? ` · ${p.adresa}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="small-label text-yellow-600">📦 Festgebunden</Label>
                <Input type="number" min={0} value={fest} onChange={(e) => setFest(e.target.value)} placeholder="0" className="mt-1" data-testid="emp-bags-fest" autoFocus/>
                <div className="text-[10px] text-zinc-400 mt-1">strâns sigilat</div>
              </div>
              <div>
                <Label className="small-label text-red-600">📦 Schwachgebunden</Label>
                <Input type="number" min={0} value={schwach} onChange={(e) => setSchwach(e.target.value)} placeholder="0" className="mt-1" data-testid="emp-bags-schwach"/>
                <div className="text-[10px] text-zinc-400 mt-1">slab sigilat</div>
              </div>
            </div>
            {totalBags > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded p-3 text-center">
                <div className="font-display text-2xl font-extrabold text-purple-700">{totalBags} saci</div>
                <div className="text-xs text-purple-600">≈ {totalKg} kg deșeuri</div>
              </div>
            )}
            <div>
              <Label className="small-label">Notă (opțional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" placeholder="ex: zid izolație etaj 2"/>
            </div>
          </>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Anulează</Button>
        <Button onClick={submit} disabled={submitting || projects.length === 0} className="bg-purple-600 hover:bg-purple-700" data-testid="emp-bags-submit">
          {submitting ? "Se salvează…" : "Înregistrează"}
        </Button>
      </DialogFooter>
    </ModalShell>
  );
}

function DefectModal({ onClose }) {
  const [targetType, setTargetType] = useState("car"); // car | equipment
  const [cars, setCars] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [targetId, setTargetId] = useState("");
  const [reason, setReason] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [drivable, setDrivable] = useState(false);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [imageIds, setImageIds] = useState([]);
  const [videoIds, setVideoIds] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/cars"), api.get("/inventory")]).then(([c, i]) => {
      setCars((c.data || []).filter((x) => x.status !== "defect"));
      setEquipment((i.data || []).filter((x) => x.item_type === "echipament" && x.status !== "defect"));
    }).catch(() => {});
  }, []);

  const list = useMemo(() => {
    const src = targetType === "car" ? cars : equipment;
    const q = search.trim().toLowerCase();
    if (!q) return src;
    return src.filter((x) => {
      const hay = targetType === "car"
        ? `${x.marca || ""} ${x.model || ""} ${x.numar_inmatriculare || ""} ${x.intern_number || ""}`
        : `${x.name || ""} ${x.location || ""} ${x.serial || ""} ${x.serial_number || ""}`;
      return hay.toLowerCase().includes(q);
    });
  }, [targetType, cars, equipment, search]);

  const submit = async () => {
    if (!targetId) return toast.error("Selectează ce s-a stricat");
    if (!reason.trim()) return toast.error("Descrie defectul");
    setSubmitting(true);
    try {
      await api.post("/me/report-defect", {
        target_type: targetType,
        target_id: targetId,
        reason: reason.trim(),
        severity,
        drivable: targetType === "car" ? drivable : false,
        image_ids: imageIds,
        video_ids: videoIds,
      });
      toast.success("Defect raportat. Mecanicul va prelua reparația.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setSubmitting(false); }
  };

  return (
    <ModalShell title="⚠️ Raportează defect" onClose={onClose} maxW="max-w-xl" swissBlack>
      <div className="space-y-4">
        <div className="flex gap-2">
          {[
            { v: "car", l: "🚗 Autoturism" },
            { v: "equipment", l: "🔧 Echipament" },
          ].map((t) => (
            <button key={t.v} type="button" onClick={() => { setTargetType(t.v); setTargetId(""); }}
              className={`flex-1 py-2.5 text-sm font-bold uppercase tracking-wide border transition-colors ${targetType === t.v ? "bg-red-600 text-white border-red-600" : "bg-transparent text-white/70 border-white/20 hover:border-white/50"}`}
              data-testid={`emp-defect-type-${t.v}`}>{t.l}</button>
          ))}
        </div>
        <Input placeholder={targetType === "car" ? "Caută după număr înmatriculare/marcă…" : "Caută după nume sau serie…"} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="emp-defect-search"/>
        {targetType === "equipment" ? (
          <div className="max-h-[42vh] overflow-y-auto border border-white/10 p-2 bg-white/[0.02]">
            {list.length === 0 ? (
              <div className="p-4 text-center text-sm text-white/40">Nu există echipamente funcționale.</div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {list.map((x) => {
                  const serial = x.serial || x.serial_number || "";
                  const photoId = (x.photo_ids && x.photo_ids.length) ? x.photo_ids[0] : null;
                  const selected = targetId === x.id;
                  return (
                    <button
                      key={x.id}
                      type="button"
                      onClick={() => setTargetId(x.id)}
                      className={`border rounded p-1.5 text-left transition-all ${selected ? "border-red-500 bg-red-600/20 ring-2 ring-red-500/60" : "border-white/15 bg-white/[0.03] hover:border-white/40"}`}
                      data-testid={`emp-defect-item-${x.id}`}
                    >
                      {photoId ? (
                        <img
                          src={fileUrl(photoId)}
                          alt={x.name}
                          className="w-full aspect-square object-cover rounded border border-white/10 bg-black/20"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : (
                        <div className="w-full aspect-square rounded border border-white/10 bg-white/[0.04] flex items-center justify-center text-white/30">
                          <Package size={32}/>
                        </div>
                      )}
                      <div className="mt-1 text-[11px] font-bold leading-tight line-clamp-2 text-white" title={x.name}>{x.name}</div>
                      {x.location && (
                        <div className="text-[9px] text-white/40 truncate" title={x.location}>{x.location}</div>
                      )}
                      {serial && (
                        <div className="text-[9px] font-mono text-amber-200 truncate" title={`S/N: ${serial}`}>S/N: {serial}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
        <div className="max-h-48 overflow-y-auto border border-white/10">
          {list.length === 0 ? (
            <div className="p-4 text-center text-sm text-white/40">Nu există {targetType === "car" ? "autoturisme" : "echipamente"} funcționale.</div>
          ) : (
            list.map((x) => {
              const serial = x.serial || x.serial_number || "";
              return (
                <button key={x.id} type="button" onClick={() => setTargetId(x.id)}
                  className={`w-full text-left px-3 py-2 text-sm border-b border-white/5 last:border-0 transition-colors ${targetId === x.id ? "bg-red-600/20 font-bold text-red-400" : "text-white/70 hover:bg-white/5"}`}
                  data-testid={`emp-defect-item-${x.id}`}>
                  {targetType === "car" ? (
                    <>{`${x.intern_number || x.numar_inmatriculare || ""} · ${x.marca || ""} ${x.model || ""}`.trim()}</>
                  ) : (
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span>
                        {x.name}
                        {x.location && <span className="text-white/40"> · {x.location}</span>}
                      </span>
                      {serial && (
                        <span className="font-mono text-[10px] bg-white/10 text-amber-200 px-1.5 py-0.5 rounded border border-white/15 whitespace-nowrap" title={`S/N: ${serial}`}>
                          S/N: {serial}
                        </span>
                      )}
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
        )}
        <div>
          <Label className="small-label">Ce s-a stricat?</Label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" placeholder="ex: nu pornește, motorul ratează, lampă spartă" data-testid="emp-defect-reason"/>
        </div>
        <div>
          <Label className="small-label">Gravitate</Label>
          <div className="flex gap-2 mt-2">
            {[
              { v: "low", l: "🟢 Mică" },
              { v: "medium", l: "🟡 Medie" },
              { v: "high", l: "🔴 Mare" },
            ].map((s) => (
              <button key={s.v} type="button" onClick={() => setSeverity(s.v)}
                className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors ${severity === s.v ? "bg-white text-black border-white" : "bg-transparent text-white/70 border-white/20 hover:border-white/50"}`}
                data-testid={`emp-defect-sev-${s.v}`}>{s.l}</button>
            ))}
          </div>
        </div>
        {targetType === "car" && (
          <div>
            <Label className="small-label">Mai poate fi condusă?</Label>
            <div className="flex gap-2 mt-2">
              <button type="button" onClick={() => setDrivable(true)}
                className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider border-2 transition-colors ${drivable ? "bg-emerald-500 text-white border-emerald-400" : "bg-transparent text-white/60 border-white/20 hover:border-emerald-400/60"}`}
                data-testid="emp-drivable-yes">✓ DA — poate circula</button>
              <button type="button" onClick={() => setDrivable(false)}
                className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider border-2 transition-colors ${!drivable ? "bg-red-600 text-white border-red-500" : "bg-transparent text-white/60 border-white/20 hover:border-red-400/60"}`}
                data-testid="emp-drivable-no">✗ NU — imobilizată</button>
            </div>
            <div className="text-[10px] text-white/40 mt-1">
              {drivable ? "Mașina rămâne disponibilă în Planificator (etichetată defect)." : "Mașina nu mai apare ca disponibilă până la reparare."}
            </div>
          </div>
        )}
        <div className="border-t border-white/10 pt-3">
          <Label className="small-label">📷 Poze defect <span className="text-white/40 normal-case">(opțional)</span></Label>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <UploadButton
              testId="emp-defect-image-upload"
              label={imageIds.length > 0 ? `+ Mai adaugă (${imageIds.length})` : "📷 Adaugă poză"}
              accept="image/*"
              onUploaded={(f) => setImageIds((prev) => [...prev, f.id])}
            />
            {imageIds.length > 0 && (
              <button
                type="button"
                onClick={() => setImageIds([])}
                className="text-[11px] text-red-400 hover:underline"
                data-testid="emp-defect-images-clear"
              >
                🗑 Șterge toate
              </button>
            )}
          </div>
          {imageIds.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-2" data-testid="emp-defect-images-preview">
              {imageIds.map((fid, idx) => (
                <div key={fid} className="relative group rounded overflow-hidden border border-white/15 aspect-square bg-white/5">
                  <AuthImg fileId={fid} className="w-full h-full object-cover" alt={`Poză defect ${idx + 1}`} />
                  <button
                    type="button"
                    onClick={() => setImageIds((prev) => prev.filter((x) => x !== fid))}
                    className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] opacity-100"
                    title="Șterge"
                    data-testid={`emp-defect-img-remove-${idx}`}
                  >
                    ✗
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border-t border-white/10 pt-3">
          <Label className="small-label">🎬 Filmare defect <span className="text-white/40 normal-case">(opțional · mp4, mov, max ~50 MB)</span></Label>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <UploadButton
              testId="emp-defect-video-upload"
              label={videoIds.length > 0 ? `+ Mai adaugă filmare (${videoIds.length})` : "🎬 Adaugă filmare"}
              accept="video/*"
              onUploaded={(f) => setVideoIds((prev) => [...prev, f.id])}
            />
            {videoIds.length > 0 && (
              <button
                type="button"
                onClick={() => setVideoIds([])}
                className="text-[11px] text-red-400 hover:underline"
                data-testid="emp-defect-videos-clear"
              >
                🗑 Șterge toate
              </button>
            )}
          </div>
          {videoIds.length > 0 && (
            <ul className="space-y-1.5 mt-2" data-testid="emp-defect-videos-preview">
              {videoIds.map((fid, idx) => (
                <li key={fid} className="flex items-center justify-between gap-2 p-2 rounded border border-white/15 bg-white/5">
                  <div className="flex items-center gap-2 text-xs text-white/80 min-w-0 flex-1">
                    <span className="text-base">🎬</span>
                    <span className="truncate">Filmare #{idx + 1}</span>
                    <span className="text-[10px] text-emerald-300 font-bold uppercase">Atașată</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setVideoIds((prev) => prev.filter((x) => x !== fid))}
                    className="text-[11px] text-red-400 hover:text-red-300"
                    title="Șterge filmarea"
                    data-testid={`emp-defect-video-remove-${idx}`}
                  >
                    ✗ Șterge
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <DialogFooter className="mt-6 gap-2">
        <button type="button" onClick={onClose} className="sb-outline-btn">Anulează</button>
        <button type="button" onClick={submit} disabled={submitting} className="sb-primary-btn" data-testid="emp-defect-submit">
          {submitting ? "Se trimite…" : "Raportează →"}
        </button>
      </DialogFooter>
    </ModalShell>
  );
}

function InventoryTakeModal({ onClose }) {
  const { user } = useAuth();
  const isPriv = user?.role === "admin" || user?.role === "magazioner";
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [bucket, setBucket] = useState(null); // null | "consumabile" | "equipment"
  const [picked, setPicked] = useState(null); // selected item
  const [pickedCategory, setPickedCategory] = useState(null); // { name, items }
  const [qty, setQty] = useState("1");
  const [note, setNote] = useState("");
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("__none__");
  const [submitting, setSubmitting] = useState(false);
  const [zoomPhotoId, setZoomPhotoId] = useState(null); // lightbox tap-to-zoom pe miniaturi
  const [zoomPhotos, setZoomPhotos] = useState([]);     // lista completă de poze a itemului activ
  const [zoomIndex, setZoomIndex] = useState(0);

  const openZoom = (photoIds, startId) => {
    const ids = Array.isArray(photoIds) ? photoIds.filter(Boolean) : [];
    if (ids.length === 0) return;
    const idx = Math.max(0, ids.findIndex((p) => p === startId));
    setZoomPhotos(ids);
    setZoomIndex(idx === -1 ? 0 : idx);
    setZoomPhotoId(ids[idx === -1 ? 0 : idx]);
  };
  const closeZoom = () => { setZoomPhotoId(null); setZoomPhotos([]); setZoomIndex(0); };
  const zoomPrev = () => {
    if (zoomPhotos.length < 2) return;
    const ni = (zoomIndex - 1 + zoomPhotos.length) % zoomPhotos.length;
    setZoomIndex(ni); setZoomPhotoId(zoomPhotos[ni]);
  };
  const zoomNext = () => {
    if (zoomPhotos.length < 2) return;
    const ni = (zoomIndex + 1) % zoomPhotos.length;
    setZoomIndex(ni); setZoomPhotoId(zoomPhotos[ni]);
  };

  // Keyboard navigation while lightbox open
  useEffect(() => {
    if (!zoomPhotoId) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeZoom();
      else if (e.key === "ArrowLeft") zoomPrev();
      else if (e.key === "ArrowRight") zoomNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomPhotoId, zoomIndex, zoomPhotos]);

  useEffect(() => {
    Promise.all([api.get("/inventory"), api.get("/me/active-projects")]).then(([inv, pr]) => {
      let list = (inv.data || []).filter((x) => (x.quantity || 0) > 0 && x.status !== "defect");
      // Angajații obișnuiți (nu admin/magazioner) NU văd:
      //  - consumabilele cu locația „Auto Magazie"
      //  - PSA (echipamente de protecție) — se alocă doar prin admin/magazioner
      // Doar admin și magazioner văd toate categoriile.
      if (!isPriv) {
        list = list.filter((x) => {
          const itype = (x.item_type || "echipament").toLowerCase();
          const loc = (x.location || "").trim().toLowerCase();
          if (itype === "psa") return false;
          if (itype === "consumabil" && loc === "auto magazie") return false;
          return true;
        });
      }
      setItems(list);
      setProjects(pr.data.projects || []);
    }).catch(() => {});
  }, [isPriv]);

  // Clasifică itemii pe bucket: "consumabile" = item_type "consumabil"; "equipment" = restul (echipament/psa/piesa_auto)
  const itemBucket = (it) => ((it?.item_type || "echipament").toLowerCase() === "consumabil" ? "consumabile" : "equipment");

  const bucketCounts = useMemo(() => {
    const c = { consumabile: 0, equipment: 0 };
    for (const it of items) c[itemBucket(it)] += 1;
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = items;
    if (q) arr = arr.filter((x) => `${x.name || ""} ${x.serial || ""} ${x.location || ""} ${x.note || ""}`.toLowerCase().includes(q));
    if (bucket) arr = arr.filter((x) => itemBucket(x) === bucket);
    return arr;
  }, [items, search, bucket]);

  // Group filtered items by name (category)
  const categories = useMemo(() => {
    const map = new Map();
    for (const it of filtered) {
      const key = (it.name || "—").trim();
      if (!map.has(key)) map.set(key, { name: key, items: [] });
      map.get(key).items.push(it);
    }
    const out = Array.from(map.values()).map((g) => {
      const totalQty = g.items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
      const types = Array.from(new Set(g.items.map((i) => i.item_type || "echipament")));
      const sample = g.items[0];
      return { ...g, totalQty, count: g.items.length, types, sample };
    });
    out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }, [filtered]);

  // When the user types and a category gets opened, keep it in sync (close it if no longer in filtered list)
  useEffect(() => {
    if (!pickedCategory) return;
    const stillExists = categories.find((c) => c.name === pickedCategory.name);
    if (stillExists) setPickedCategory(stillExists);
    else setPickedCategory(null);
  }, [categories, pickedCategory]);

  const submit = async () => {
    if (!picked) return toast.error("Selectează un produs");
    const q = parseFloat(qty);
    if (!q || q <= 0) return toast.error("Cantitate invalidă");
    if (q > picked.quantity) return toast.error(`Disponibil: ${picked.quantity}`);
    setSubmitting(true);
    try {
      await api.post(`/inventory/${picked.id}/issue`, {
        quantity: q,
        note: note || null,
        project_id: projectId === "__none__" ? null : projectId,
      });
      toast.success(`Preluat: ${q} ${picked.unit || ""} ${picked.name}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setSubmitting(false); }
  };

  const typeLabels = { echipament: "Echipament", psa: "PSA", consumabil: "Consumabil", piesa_auto: "Piesă auto" };

  return (
    <ModalShell title="📦 Ia din Inventar" onClose={onClose} maxW="max-w-3xl">
      {!picked ? (
        <div className="space-y-3">
          {/* Search bar — stays at the top */}
          <Input
            placeholder="Caută în inventar (nume, serie, locație)…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPickedCategory(null); }}
            autoFocus
            data-testid="emp-inv-search"
          />

          {!bucket && !search.trim() ? (
            // Phase 0: macro-bucket selection (CONSUMABILE vs ECHIPAMENTE & PSA)
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <button
                type="button"
                onClick={() => { setBucket("consumabile"); setPickedCategory(null); }}
                disabled={bucketCounts.consumabile === 0}
                className="text-left border-2 rounded-lg p-5 hover:border-amber-500 hover:shadow-md transition-all bg-gradient-to-br from-amber-50 to-white disabled:opacity-50 disabled:cursor-not-allowed flex flex-col gap-2"
                data-testid="emp-inv-bucket-consumabile"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-amber-100 border-2 border-amber-300 grid place-items-center flex-shrink-0">
                    <span className="text-2xl">🧴</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-extrabold uppercase tracking-wider text-amber-900">CONSUMABILE</div>
                    <div className="text-[11px] text-amber-700/80">Articole de uz curent (vopsea, șuruburi, mănuși de unică folosință, etc.)</div>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-amber-200">
                  <span className="text-[10px] uppercase text-amber-700 font-bold">Articole disponibile</span>
                  <span className="text-2xl font-extrabold text-amber-700 tabular-nums">{bucketCounts.consumabile}</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => { setBucket("equipment"); setPickedCategory(null); }}
                disabled={bucketCounts.equipment === 0}
                className="text-left border-2 rounded-lg p-5 hover:border-sky-500 hover:shadow-md transition-all bg-gradient-to-br from-sky-50 to-white disabled:opacity-50 disabled:cursor-not-allowed flex flex-col gap-2"
                data-testid="emp-inv-bucket-equipment"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-sky-100 border-2 border-sky-300 grid place-items-center flex-shrink-0">
                    <span className="text-2xl">🦺</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-extrabold uppercase tracking-wider text-sky-900">{isPriv ? "ECHIPAMENTE & PSA" : "ECHIPAMENTE"}</div>
                    <div className="text-[11px] text-sky-700/80">{isPriv ? "Echipamente reutilizabile, PSA, piese auto, scule." : "Echipamente reutilizabile, piese auto, scule."}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-sky-200">
                  <span className="text-[10px] uppercase text-sky-700 font-bold">Articole disponibile</span>
                  <span className="text-2xl font-extrabold text-sky-700 tabular-nums">{bucketCounts.equipment}</span>
                </div>
              </button>
            </div>
          ) : !pickedCategory ? (
            // Phase A: category cards grid
            categories.length === 0 ? (
              <>
                {bucket && !search.trim() && (
                  <button
                    type="button"
                    onClick={() => setBucket(null)}
                    className="text-sky-600 hover:underline text-xs"
                    data-testid="emp-inv-bucket-back"
                  >
                    ← Schimbă categoria principală
                  </button>
                )}
                <div className="p-6 text-center text-sm text-zinc-400 border rounded">
                  {search ? `Niciun produs pentru „${search}"` : "Niciun produs disponibil."}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  {bucket && !search.trim() ? (
                    <button
                      type="button"
                      onClick={() => setBucket(null)}
                      className="text-sky-600 hover:underline text-xs font-semibold"
                      data-testid="emp-inv-bucket-back"
                    >
                      ← {bucket === "consumabile" ? "CONSUMABILE" : (isPriv ? "ECHIPAMENTE & PSA" : "ECHIPAMENTE")} · Schimbă
                    </button>
                  ) : <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-bold pt-1">Categorii ({categories.length})</span>}
                  {bucket && !search.trim() && (
                    <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-bold">Categorii ({categories.length})</span>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[55vh] overflow-y-auto pr-1">
                  {categories.map((c) => {
                    const typeLabel = typeLabels[c.types[0]] || c.types[0] || "";
                    const isPsa = c.types.includes("psa");
                    return (
                      <button
                        key={c.name}
                        type="button"
                        onClick={() => {
                          if (c.items.length === 1) {
                            // Single item — go straight to qty form
                            setPicked(c.items[0]);
                            setQty("1");
                          } else {
                            setPickedCategory(c);
                          }
                        }}
                        data-testid={`emp-inv-cat-${c.name}`}
                        className="text-left border rounded-md p-3 hover:border-sky-500 hover:shadow-sm transition-all bg-white flex flex-col gap-1.5"
                        title={`${c.name} — ${c.totalQty} ${c.sample?.unit || "buc"} disponibil${c.count > 1 ? ` (${c.count} carduri)` : ""}`}
                      >
                        <div className="flex items-start gap-2">
                          {(() => {
                            // Caută prima poză disponibilă în vreuna din variantele acestei categorii
                            const itemWithPhoto = (c.items || []).find((it) => Array.isArray(it.photo_ids) && it.photo_ids.length > 0);
                            const photoId = itemWithPhoto ? itemWithPhoto.photo_ids[0] : null;
                            if (photoId) {
                              return (
                                <img
                                  src={fileUrl(photoId)}
                                  alt={c.name}
                                  className="w-12 h-12 object-cover rounded-md border border-sky-200 flex-shrink-0 bg-white"
                                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                              );
                            }
                            return (
                              <div className="w-12 h-12 rounded-md bg-sky-50 border border-sky-200 grid place-items-center flex-shrink-0">
                                <Package size={20} className="text-sky-700"/>
                              </div>
                            );
                          })()}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">{c.name}</div>
                            <div className="text-[10px] text-zinc-500 flex items-center gap-1.5 mt-0.5">
                              {isPsa && <span className="px-1 py-0.5 rounded bg-cyan-100 text-cyan-800 font-bold text-[9px]">PSA</span>}
                              <span>{typeLabel}{c.types.length > 1 ? " +" : ""}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs font-bold text-sky-700 tabular-nums">
                            {c.totalQty.toLocaleString("ro-RO")} {c.sample?.unit || "buc"}
                          </span>
                          <span className="text-[10px] text-zinc-400">
                            {c.count > 1 ? `${c.count} variante` : "1 articol"}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )
          ) : (
            // Phase B: items inside the selected category
            <>
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPickedCategory(null)}
                    className="text-sky-600 hover:underline text-xs"
                    data-testid="emp-inv-cat-back"
                  >
                    ← Categorii
                  </button>
                  <span className="text-zinc-400">/</span>
                  <span>{pickedCategory.name}</span>
                </div>
                <span className="text-[11px] text-zinc-500">
                  {pickedCategory.count} variante · {pickedCategory.totalQty.toLocaleString("ro-RO")} {pickedCategory.sample?.unit || "buc"}
                </span>
              </div>
              <div className="max-h-[55vh] overflow-y-auto border rounded">
                {pickedCategory.items.map((x) => {
                  const photoId = (x.photo_ids && x.photo_ids.length) ? x.photo_ids[0] : null;
                  return (
                  <button
                    key={x.id}
                    type="button"
                    onClick={() => { setPicked(x); setQty("1"); }}
                    className="w-full text-left px-3 py-2 text-sm border-b last:border-0 hover:bg-sky-50 flex items-center gap-3"
                    data-testid={`emp-inv-pick-${x.id}`}
                  >
                    {photoId ? (
                      <img
                        src={fileUrl(photoId)}
                        alt={x.name}
                        className="w-14 h-14 object-cover rounded border border-zinc-200 flex-shrink-0 bg-zinc-50 cursor-zoom-in hover:opacity-80"
                        onClick={(e) => { e.stopPropagation(); openZoom(x.photo_ids || [], photoId); }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        data-testid={`emp-inv-thumb-${x.id}`}
                      />
                    ) : (
                      <div className="w-14 h-14 rounded border border-zinc-200 bg-zinc-100 flex items-center justify-center flex-shrink-0 text-zinc-400" aria-hidden="true">
                        <Package size={22}/>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{x.name}</div>
                      {x.serial && (
                        <div className="text-[11px] font-mono font-bold text-amber-800 mt-0.5" title="Număr de serie">
                          🏷️ #{x.serial}
                        </div>
                      )}
                      <div className="text-[10px] text-zinc-500 mt-0.5">
                        📍 {x.location || "—"}
                        {x.shelf_row && x.shelf_col ? ` · ${x.shelf_row}${x.shelf_col}` : ""}
                        {" · "}{typeLabels[x.item_type] || x.item_type}
                        {x.color ? ` · ${x.color}` : ""}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-bold text-sky-700 tabular-nums">{x.quantity}{x.unit ? ` ${x.unit}` : ""}</div>
                    </div>
                  </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="bg-sky-50 border border-sky-200 rounded p-3 flex gap-3 items-start">
            {(picked.photo_ids && picked.photo_ids.length) ? (
              <img
                src={fileUrl(picked.photo_ids[0])}
                alt={picked.name}
                className="w-20 h-20 object-cover rounded border-2 border-sky-300 flex-shrink-0 bg-white cursor-zoom-in hover:opacity-80"
                onClick={() => openZoom(picked.photo_ids || [], picked.photo_ids[0])}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                data-testid="emp-inv-picked-thumb"
              />
            ) : null}
            <div className="min-w-0 flex-1">
              <div className="font-bold text-base">{picked.name}</div>
              {picked.serial && (
                <div className="text-[11px] font-mono font-bold text-amber-800 mt-1" title="Număr de serie">
                  🏷️ #{picked.serial}
                </div>
              )}
              <div className="text-xs text-zinc-600 mt-1">📍 {picked.location || "—"}{picked.shelf_row && picked.shelf_col ? ` · ${picked.shelf_row}${picked.shelf_col}` : ""}</div>
              <div className="text-xs text-zinc-600 mt-1">Disponibil: <span className="font-bold text-sky-700">{picked.quantity}{picked.unit ? ` ${picked.unit}` : ""}</span></div>
              <button type="button" onClick={() => setPicked(null)} className="text-[11px] text-sky-600 underline mt-1">← schimbă produsul</button>
            </div>
          </div>
          <div>
            <Label className="small-label">Cantitate de preluat ({picked.unit || "buc"})</Label>
            <Input type="number" step="0.01" min={0} max={picked.quantity} value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1" autoFocus data-testid="emp-inv-qty"/>
          </div>
          {projects.length > 0 && (
            <div>
              <Label className="small-label">Pentru proiect (opțional)</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger className="mt-1" data-testid="emp-inv-project"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Niciunul —</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="small-label">Notă (opțional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" placeholder="ex: pentru lucrarea X"/>
          </div>
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Anulează</Button>
        {picked && (
          <Button onClick={submit} disabled={submitting} className="bg-sky-600 hover:bg-sky-700" data-testid="emp-inv-submit">
            {submitting ? "Se preia…" : "Preia"}
          </Button>
        )}
      </DialogFooter>

      {/* Lightbox tap-to-zoom — overlay simplu read-only deasupra modalului */}
      {zoomPhotoId && (() => {
        let touchStartX = 0;
        const onTouchStart = (e) => { touchStartX = e.touches[0]?.clientX || 0; };
        const onTouchEnd = (e) => {
          const dx = (e.changedTouches[0]?.clientX || 0) - touchStartX;
          if (Math.abs(dx) < 40) return;
          if (dx > 0) zoomPrev(); else zoomNext();
        };
        const hasMultiple = zoomPhotos.length > 1;
        return (
        <div
          role="dialog"
          aria-label="Imagine echipament"
          onClick={closeZoom}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center cursor-zoom-out p-4 select-none"
          data-testid="emp-inv-zoom-overlay"
        >
          <button
            type="button"
            aria-label="Închide imaginea"
            onClick={(e) => { e.stopPropagation(); closeZoom(); }}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white text-xl font-bold flex items-center justify-center"
            data-testid="emp-inv-zoom-close"
          >
            ✕
          </button>
          {hasMultiple && (
            <>
              <button
                type="button"
                aria-label="Imaginea anterioară"
                onClick={(e) => { e.stopPropagation(); zoomPrev(); }}
                className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 text-white text-2xl font-bold flex items-center justify-center"
                data-testid="emp-inv-zoom-prev"
              >
                ‹
              </button>
              <button
                type="button"
                aria-label="Imaginea următoare"
                onClick={(e) => { e.stopPropagation(); zoomNext(); }}
                className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 text-white text-2xl font-bold flex items-center justify-center"
                data-testid="emp-inv-zoom-next"
              >
                ›
              </button>
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/15 text-white text-sm font-bold"
                data-testid="emp-inv-zoom-counter"
              >
                {zoomIndex + 1} / {zoomPhotos.length}
              </div>
            </>
          )}
          <img
            src={fileUrl(zoomPhotoId)}
            alt="Echipament — vizualizare mărită"
            className="max-w-full max-h-[90vh] object-contain rounded shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        );
      })()}
    </ModalShell>
  );
}

function RepairModal({ onClose }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(null); // { type, item }
  const [spareParts, setSpareParts] = useState([]);
  const [selectedParts, setSelectedParts] = useState([]);
  const [oilLiters, setOilLiters] = useState("0");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/me/defective-items").then((r) => setData(r.data)).catch(() => setData({ cars: [], equipment: [] }));
  }, []);

  useEffect(() => {
    if (!picked) { setSpareParts([]); return; }
    const url = picked.type === "car" ? `/cars/${picked.item.id}/spare-parts` : `/inventory/${picked.item.id}/spare-parts`;
    api.get(url).then((r) => setSpareParts(r.data || [])).catch(() => setSpareParts([]));
    setSelectedParts([]);
  }, [picked]);

  const submit = async () => {
    if (!picked) return toast.error("Selectează un articol defect");
    setSubmitting(true);
    try {
      const url = picked.type === "car" ? `/cars/${picked.item.id}/repair` : `/inventory/${picked.item.id}/repair`;
      const r = await api.post(url, {
        spare_part_ids: selectedParts,
        oil_liters: picked.type === "car" ? (parseFloat(oilLiters) || 0) : 0,
        note: note || null,
      });
      const shortage = r.data?.oil_shortage || 0;
      toast.success(shortage > 0 ? `Reparat. ⚠ Lipsă ulei: ${shortage} L` : "Reparat cu succes");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setSubmitting(false); }
  };

  if (!data) return <ModalShell title="🔧 Repară" onClose={onClose}><div className="text-zinc-400">Se încarcă…</div></ModalShell>;

  return (
    <ModalShell title="🔧 Repară" onClose={onClose} maxW="max-w-2xl">
      {!picked ? (
        <div className="space-y-3">
          <p className="text-xs text-zinc-500">Articole defecte (sortate cronologic):</p>
          {data.cars.length === 0 && data.equipment.length === 0 ? (
            <div className="text-center py-6 text-sm text-green-700 bg-green-50 border border-green-200 rounded">
              ✅ Nimic defect. Toate sunt funcționale!
            </div>
          ) : (
            <>
              {data.cars.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase font-bold text-zinc-500 mb-1">Autoturisme defecte</div>
                  <div className="space-y-1.5">
                    {data.cars.map((c) => (
                      <button key={c.id} type="button" onClick={() => setPicked({ type: "car", item: c })}
                        className="w-full text-left card-flat p-3 hover:border-orange-500 transition-colors"
                        data-testid={`emp-rep-car-${c.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="font-bold">🚗 {c.display}</div>
                          {c.defect_marked_at && <div className="text-[10px] text-zinc-400">{c.defect_marked_at.slice(0,10)}</div>}
                        </div>
                        {c.defect_reason && <div className="text-xs text-red-600 mt-1">⚠ {c.defect_reason}</div>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {data.equipment.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase font-bold text-zinc-500 mb-1">Echipamente defecte</div>
                  <div className="space-y-1.5">
                    {data.equipment.map((e) => (
                      <button key={e.id} type="button" onClick={() => setPicked({ type: "equipment", item: e })}
                        className="w-full text-left card-flat p-3 hover:border-orange-500 transition-colors"
                        data-testid={`emp-rep-eq-${e.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="font-bold">🔧 {e.name}</div>
                          {e.warranty_until && <div className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">📋 Garanție: {String(e.warranty_until).slice(0,10)}</div>}
                        </div>
                        {e.defect_reason && <div className="text-xs text-red-600 mt-1">⚠ {e.defect_reason}</div>}
                        <div className="text-[10px] text-zinc-500 mt-1">{e.location || "—"}{e.supplier ? ` · ${e.supplier}` : ""}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="bg-orange-50 border border-orange-200 rounded p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold">{picked.type === "car" ? `🚗 ${picked.item.display}` : `🔧 ${picked.item.name}`}</div>
                {picked.item.defect_reason && <div className="text-xs text-red-600 mt-1">⚠ {picked.item.defect_reason}</div>}
                {picked.type === "equipment" && picked.item.warranty_until && (
                  <div className="text-xs text-emerald-700 mt-1 font-semibold">📋 Sub garanție până: {String(picked.item.warranty_until).slice(0,10)} — verifică retur la furnizor!</div>
                )}
              </div>
              <button type="button" onClick={() => setPicked(null)} className="text-xs text-orange-600 underline">← schimbă</button>
            </div>
          </div>

          <div>
            <Label className="small-label">
              Piese consumate (din inventar) <span className="text-[10px] text-zinc-400 font-normal normal-case italic">— opțional</span>
            </Label>
            {spareParts.length === 0 ? (
              <div className="text-xs text-zinc-400 italic mt-1">Reparația se poate face și fără piese — completează doar nota.</div>
            ) : (
              <div className="mt-1 max-h-40 overflow-y-auto border rounded">
                {spareParts.map((p) => {
                  const sel = selectedParts.includes(p.id);
                  return (
                    <button key={p.id} type="button"
                      onClick={() => setSelectedParts((x) => sel ? x.filter((i) => i !== p.id) : [...x, p.id])}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm border-b last:border-0 ${sel ? "bg-orange-100" : "hover:bg-zinc-50"}`}
                      data-testid={`emp-rep-part-${p.id}`}>
                      <span>{sel ? "✓ " : "○ "}{p.name}</span>
                      <span className="text-[10px] text-zinc-500">stoc: {p.quantity}{p.unit ? ` ${p.unit}` : ""}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {picked.type === "car" && (
            <div>
              <Label className="small-label">Ulei consumat (litri)</Label>
              <Input type="number" step="0.1" min={0} value={oilLiters} onChange={(e) => setOilLiters(e.target.value)} className="mt-1" data-testid="emp-rep-oil"/>
            </div>
          )}

          <div>
            <Label className="small-label">Notă reparație</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" placeholder="ex: schimbat ulei + filtru" data-testid="emp-rep-note"/>
          </div>
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Închide</Button>
        {picked && (
          <Button onClick={submit} disabled={submitting} className="bg-orange-600 hover:bg-orange-700" data-testid="emp-rep-submit">
            {submitting ? "Se procesează…" : "Marchează reparat"}
          </Button>
        )}
      </DialogFooter>
    </ModalShell>
  );
}

// ===== SUVA WORKFLOW: Start zonă / Finalizează / Demontaj =====

function MissingRevizieBanner({ navigate }) {
  const [cars, setCars] = useState([]);
  const [serviceCar, setServiceCar] = useState(null);
  const fetchCars = () => api.get("/dashboard/alerts")
    .then((r) => setCars(r.data?.cars_missing_revizie || []))
    .catch(() => setCars([]));
  useEffect(() => { fetchCars(); }, []);
  useSyncWatch(["inventory", "persons"], fetchCars);
  if (!cars.length && !serviceCar) return null;
  return (
    <>
    {cars.length > 0 && (
    <div
      data-testid="emp-missing-revizie"
      className="card-flat p-4 border-2 border-red-600 bg-red-50 animate-pulse-red-soft"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-md bg-red-600 grid place-items-center text-white animate-pulse">
          <Gauge size={20}/>
        </div>
        <div className="flex-1">
          <div className="small-label text-red-700 font-bold">Autoturisme fără dată ultimă revizie</div>
          <div className="font-display text-2xl font-extrabold leading-tight text-red-700">{cars.length}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[180px] overflow-y-auto">
        {cars.map((c) => (
          <div
            key={c.id}
            className="rounded border border-red-300 bg-white p-2 hover:bg-red-100 transition-colors"
            data-testid={`emp-missing-revizie-${c.id}`}
          >
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <button
                onClick={() => navigate(`/autoturisme/${c.id}`)}
                className="text-left flex-1 min-w-0"
                title="Deschide pagina mașinii"
              >
                <div className="text-xs font-bold text-red-900 truncate">
                  {c.intern_number ? `Auto ${c.intern_number} · ` : ""}{c.numar_inmatriculare || c.label || "—"}
                </div>
                {c.label && c.numar_inmatriculare && (
                  <div className="text-[11px] text-red-700 mt-0.5 italic truncate">{c.label}</div>
                )}
              </button>
              <ArrowRight size={12} className="text-red-400 shrink-0"/>
            </div>
            <button
              type="button"
              onClick={() => setServiceCar(c)}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-bold py-1 px-2 rounded inline-flex items-center justify-center gap-1"
              data-testid={`emp-missing-revizie-do-service-${c.id}`}
            >
              <Wrench size={11}/> Fă revizie
            </button>
          </div>
        ))}
      </div>
    </div>
    )}
    {serviceCar && (
      <RepairOrServiceDialog
        car={serviceCar}
        mode="service"
        onClose={() => setServiceCar(null)}
        onDone={() => { setServiceCar(null); fetchCars(); }}
      />
    )}
    </>
  );
}


// === Revizii SCADENTE — mașini cu km peste pragul next_service_km ===
function ServiceDueBanner({ navigate }) {
  const [cars, setCars] = useState([]);
  const [serviceCar, setServiceCar] = useState(null);
  const fetchCars = () => api.get("/dashboard/alerts")
    .then((r) => setCars(r.data?.revizie_due || []))
    .catch(() => setCars([]));
  useEffect(() => { fetchCars(); }, []);
  useSyncWatch(["inventory", "persons"], fetchCars);
  if (!cars.length && !serviceCar) return null;
  return (
    <>
    {cars.length > 0 && (
    <div
      data-testid="emp-service-due"
      className="card-flat p-4 border-2 border-amber-500 bg-amber-50"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-md bg-amber-500 grid place-items-center text-white animate-pulse">
          <Wrench size={20}/>
        </div>
        <div className="flex-1">
          <div className="small-label text-amber-800 font-bold">Revizii scadente · au depășit kilometrajul</div>
          <div className="font-display text-2xl font-extrabold leading-tight text-amber-800">{cars.length}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
        {cars.map((c) => {
          const carLabel = `${c.marca || ""} ${c.model || ""}`.trim();
          return (
          <div
            key={c.id}
            className="rounded border border-amber-300 bg-white p-2"
            data-testid={`emp-service-due-${c.id}`}
          >
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <button
                onClick={() => navigate(`/autoturisme/${c.id}`)}
                className="text-left flex-1 min-w-0"
                title="Deschide pagina mașinii"
              >
                <div className="text-xs font-bold text-amber-900 truncate">
                  {c.intern_number ? `Auto ${c.intern_number} · ` : ""}{c.numar_inmatriculare || "—"}
                </div>
                {carLabel && <div className="text-[11px] text-amber-700 mt-0.5 italic truncate">{carLabel}</div>}
                {c.km_over != null && (
                  <div className="text-[10px] text-red-700 mt-0.5 font-semibold">
                    ⚠ +{c.km_over.toLocaleString("ro-RO")} km peste prag
                  </div>
                )}
              </button>
              <ArrowRight size={12} className="text-amber-500 shrink-0"/>
            </div>
            <button
              type="button"
              onClick={() => setServiceCar(c)}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-bold py-1 px-2 rounded inline-flex items-center justify-center gap-1"
              data-testid={`emp-service-due-do-service-${c.id}`}
            >
              <Wrench size={11}/> Fă revizie
            </button>
          </div>
          );
        })}
      </div>
    </div>
    )}
    {serviceCar && (
      <RepairOrServiceDialog
        car={serviceCar}
        mode="service"
        onClose={() => setServiceCar(null)}
        onDone={() => { setServiceCar(null); fetchCars(); }}
      />
    )}
    </>
  );
}


function DismantleAlertBanner({ onOpen }) {
  const [count, setCount] = useState(0);
  const fetchData = () => api.get("/me/projects-to-dismantle").then((r) => setCount((r.data?.projects || []).length)).catch(() => {});
  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 60000);
    return () => clearInterval(t);
  }, []);
  useSyncWatch(["projects", "schedule"], fetchData);
  if (count <= 0) return null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left card-flat p-4 bg-orange-50 border-orange-300 hover:border-orange-500 flex items-center gap-3 animate-pulse"
      data-testid="dismantle-alert-banner"
    >
      <div className="w-10 h-10 rounded-full bg-orange-500 grid place-items-center text-white">
        <Hammer size={20}/>
      </div>
      <div className="flex-1">
        <div className="font-display font-bold text-orange-800">
          🔧 {count === 1 ? "Un șantier" : `${count} șantiere`} de demontat
        </div>
        <div className="text-xs text-orange-700">
          Confirmă că ai ridicat toate echipamentele lăsate de echipa anterioară.
        </div>
      </div>
      <span className="text-xs font-bold text-orange-700">Deschide →</span>
    </button>
  );
}

function StartZoneModal({ onClose }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [zoneLabel, setZoneLabel] = useState("");
  const [note, setNote] = useState("");
  const [video, setVideo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/me/active-projects").then((r) => {
      const list = r.data?.projects || [];
      setProjects(list);
      if (list.length === 1) setProjectId(list[0].id);
    }).catch(() => {});
  }, []);

  const proj = projects.find((p) => p.id === projectId);
  const zones = (proj?.zones || []).map((z, i) => ({
    label: `${z.type || "Zonă"}${z.description ? ` · ${z.description}` : ""}${z.count ? ` (${z.count})` : ""}`,
    key: `${z.type || "zona"}-${i}`,
  }));

  const submit = async () => {
    if (!projectId) return toast.error("Selectează șantierul");
    if (!video) return toast.error("Filmulețul SUVA e obligatoriu");
    setSubmitting(true);
    try {
      await api.post(`/me/projects/${projectId}/start-zone`, {
        file_id: video.id,
        zone_label: zoneLabel || null,
        note: note || null,
      });
      toast.success("Start zonă confirmat. Filmuleț SUVA salvat.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setSubmitting(false); }
  };

  return (
    <ModalShell title="🎥 Start zonă (SUVA)" onClose={onClose} maxW="max-w-lg">
      <div className="space-y-3">
        {projects.length === 0 ? (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
            Nu ești atribuit la niciun proiect activ. Cere planificatorului să te aloce.
          </div>
        ) : (
          <>
            <div>
              <Label className="small-label">Șantier</Label>
              <Select value={projectId} onValueChange={(v) => { setProjectId(v); setZoneLabel(""); }}>
                <SelectTrigger className="mt-1" data-testid="start-zone-project"><SelectValue placeholder="Alege șantierul"/></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}{p.adresa ? ` · ${p.adresa}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {zones.length > 0 && (
              <div>
                <Label className="small-label">Zonă (opțional)</Label>
                <Select value={zoneLabel || "__none__"} onValueChange={(v) => setZoneLabel(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="mt-1" data-testid="start-zone-zone"><SelectValue placeholder="— Toată zona —"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Toată zona —</SelectItem>
                    {zones.map((z) => <SelectItem key={z.key} value={z.label}>{z.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
              <Label className="small-label text-emerald-800">🎥 Filmuleț SUVA (obligatoriu)</Label>
              <p className="text-[11px] text-emerald-700 mb-2">Filmează zona arătând respectarea cerințelor SUVA (PPE, semnalizare, etc.)</p>
              {video ? (
                <div className="text-xs text-emerald-700 font-mono flex items-center gap-2">
                  ✓ {video.original_filename || video.filename || "video"}
                  <button type="button" onClick={() => setVideo(null)} className="text-red-600 underline">schimbă</button>
                </div>
              ) : (
                <UploadButton accept="video/*" label="📹 Încarcă filmuleț" testId="start-zone-upload" onUploaded={(f) => setVideo(f)} />
              )}
            </div>
            <div>
              <Label className="small-label">Notă (opțional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" data-testid="start-zone-note" placeholder="ex: zona dezinfectată, semnalizare montată"/>
            </div>
          </>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Anulează</Button>
        {projects.length > 0 && (
          <Button onClick={submit} disabled={submitting || !video || !projectId} className="bg-emerald-600 hover:bg-emerald-700" data-testid="start-zone-submit">
            {submitting ? "Se trimite…" : "Confirmă start"}
          </Button>
        )}
      </DialogFooter>
    </ModalShell>
  );
}

function FinalizeSiteModal({ onClose }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [available, setAvailable] = useState({ my_inventory: [], team_inventory: [], from_magazie: [] });
  const [loadingItems, setLoadingItems] = useState(false);
  const [search, setSearch] = useState("");
  const [pickedItemIds, setPickedItemIds] = useState([]);
  const [photos, setPhotos] = useState([]); // [{id, original_filename}]
  const [note, setNote] = useState("");
  const [notifiedTudor, setNotifiedTudor] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/me/active-projects")
      .then((r) => setProjects(r.data?.projects || []))
      .catch(() => {});
  }, []);

  // Load available items when project changes
  useEffect(() => {
    if (!projectId) {
      setAvailable({ my_inventory: [], team_inventory: [], from_magazie: [] });
      setPickedItemIds([]);
      return;
    }
    setLoadingItems(true);
    api.get(`/me/projects/${projectId}/finalize-available-items`)
      .then((r) => setAvailable(r.data || { my_inventory: [], team_inventory: [], from_magazie: [] }))
      .catch((e) => toast.error(e?.response?.data?.detail || "Eroare la încărcarea echipamentelor"))
      .finally(() => setLoadingItems(false));
  }, [projectId]);

  const filterByQ = (arr) => {
    const q = search.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter((x) => `${x.name || ""} ${x.location || ""} ${x.holder_name || ""}`.toLowerCase().includes(q));
  };

  const myItems = useMemo(() => filterByQ(available.my_inventory || []), [available, search]);
  const teamItems = useMemo(() => filterByQ(available.team_inventory || []), [available, search]);
  const magazieItems = useMemo(() => filterByQ(available.from_magazie || []), [available, search]);

  const toggleItem = (id) => setPickedItemIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!projectId) return toast.error("Selectează șantierul");
    // Toate restricțiile sunt acum opționale — userul poate marca șantierul ca demontat
    // chiar dacă nu selectează echipamente, nu adaugă poze sau nu confirmă anunțarea Tudor.
    setSubmitting(true);
    try {
      await api.post(`/me/projects/${projectId}/finalize`, {
        photo_file_ids: photos.map((p) => p.id),
        equipment_item_ids: pickedItemIds,
        note: note || null,
        notified_tudor: notifiedTudor,
      });
      toast.success("Șantier finalizat — marcat 'De demontat'. Echipa următoare va primi reminder.");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setSubmitting(false); }
  };

  const ItemRow = ({ it, source }) => {
    const sel = pickedItemIds.includes(it.id);
    return (
      <button type="button" onClick={() => toggleItem(it.id)}
        className={`w-full text-left px-3 py-1.5 text-xs border-b last:border-0 flex items-center gap-2 ${sel ? "bg-purple-100" : "hover:bg-purple-50"}`}
        data-testid={`finalize-item-${it.id}`}>
        <span className={`w-4 h-4 rounded border ${sel ? "bg-purple-600 border-purple-600 text-white" : "border-zinc-300"} grid place-items-center text-[10px] flex-shrink-0`}>{sel ? "✓" : ""}</span>
        <span className="flex-1 min-w-0">
          <span className="font-semibold">{it.name}</span>
          <span className="text-zinc-500 ml-2">
            📍 {it.location || "—"}{it.shelf_row && it.shelf_col ? ` · ${it.shelf_row}${it.shelf_col}` : ""}
            {source === "team" && it.holder_name ? ` · 👤 ${it.holder_name}` : ""}
            {source === "magazie" && it.issued_qty ? ` · ${it.issued_qty} ${it.unit || ""} luate` : ""}
          </span>
        </span>
      </button>
    );
  };

  const Section = ({ title, color, items, source, emptyMsg }) => {
    const cls = {
      blue: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800" },
      emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800" },
      amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800" },
    }[color] || { bg: "bg-zinc-50", border: "border-zinc-200", text: "text-zinc-700" };
    return (
      <div className={`${cls.bg} border ${cls.border} rounded p-2`}>
        <Label className={`small-label ${cls.text}`}>{title} ({items.length})</Label>
        <div className={`max-h-40 overflow-y-auto border ${cls.border} rounded bg-white mt-1`}>
          {items.length === 0 ? (
            <div className="p-3 text-xs text-zinc-400 text-center">{emptyMsg}</div>
          ) : items.map((it) => <ItemRow key={`${source}-${it.id}`} it={it} source={source}/>)}
        </div>
      </div>
    );
  };

  return (
    <ModalShell title="🏁 Finalizează șantier" onClose={onClose} maxW="max-w-2xl">
      <div className="space-y-3">
        {projects.length === 0 ? (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
            Nu ești atribuit la niciun proiect activ.
          </div>
        ) : (
          <>
            <div>
              <Label className="small-label">Șantier finalizat</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger className="mt-1" data-testid="finalize-project"><SelectValue placeholder="Alege șantierul"/></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}{p.adresa ? ` · ${p.adresa}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {projectId && (
              <>
                <div className="bg-purple-50 border border-purple-200 rounded p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="small-label text-purple-800">📦 Echipamente lăsate pe șantier ({pickedItemIds.length} selectate)</Label>
                    {loadingItems && <span className="text-[11px] text-purple-600 italic">se încarcă…</span>}
                  </div>
                  <Input placeholder="Caută echipament…" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-white" data-testid="finalize-search"/>
                  <Section title="🧑 Inventarul meu" color="blue" items={myItems} source="my" emptyMsg="Nu ai inventar alocat"/>
                  <Section title="👥 Inventarul echipei (alocat colegilor)" color="emerald" items={teamItems} source="team" emptyMsg="Niciun coleg cu inventar alocat pe acest proiect"/>
                  <Section title="📦 Luate din magazie pentru acest șantier" color="amber" items={magazieItems} source="magazie" emptyMsg="Nimic luat din magazie pentru acest proiect"/>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded p-3">
                  <Label className="small-label text-amber-800">📷 Poze ({photos.length}/min 2 — finalizare + echipamente)</Label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {photos.map((p, idx) => (
                      <div key={p.id} className="relative">
                        <AuthImg fileId={p.id} className="w-full h-20 object-cover rounded border border-amber-300"/>
                        <button type="button" onClick={() => removePhoto(idx)} className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 border border-zinc-200 text-red-600">
                          <Trash2 size={10}/>
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2">
                    <UploadButton accept="image/*" label="📷 Adaugă poză" testId="finalize-photo-upload" onUploaded={(f) => setPhotos((arr) => [...arr, f])} />
                  </div>
                </div>
                <div>
                  <Label className="small-label">Notă (opțional)</Label>
                  <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" placeholder="ex: schele rămase pe poziție, generator în mijloc"/>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <Label className="small-label text-blue-800">📞 Ai anunțat pe Tudor pentru măsurătoare?</Label>
                  <div className="flex gap-2 mt-2">
                    {[
                      { v: true,  l: "✓ Da, l-am anunțat" },
                      { v: false, l: "✗ Nu încă" },
                    ].map((opt) => (
                      <button
                        key={String(opt.v)}
                        type="button"
                        onClick={() => setNotifiedTudor(opt.v)}
                        className={`flex-1 py-2 rounded text-sm font-semibold border transition-colors ${notifiedTudor === opt.v
                          ? (opt.v ? "bg-emerald-600 text-white border-emerald-600" : "bg-amber-500 text-white border-amber-500")
                          : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}
                        data-testid={`finalize-tudor-${opt.v ? "yes" : "no"}`}
                      >{opt.l}</button>
                    ))}
                  </div>
                  {notifiedTudor === false && (
                    <div className="mt-2 text-[11px] text-amber-700 italic">Te rugăm să-l anunți pe Tudor cât mai curând pentru măsurătoarea finală.</div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Anulează</Button>
        {projects.length > 0 && (
          <Button onClick={submit} disabled={submitting} className="bg-purple-600 hover:bg-purple-700" data-testid="finalize-submit">
            {submitting ? "Se trimite…" : "Marchează 'De demontat'"}
          </Button>
        )}
      </DialogFooter>
    </ModalShell>
  );
}

function DismantleListModal({ onClose }) {
  const [projects, setProjects] = useState([]);
  const [picked, setPicked] = useState(null);
  const [meta, setMeta] = useState({ locations: [], colors: [], shelf_locations: [], rows: [] });
  // returnsByEq[eq.id] = { photo: {id, original_filename}, return_location, return_color, return_shelf_row, return_shelf_col, location_changed }
  const [returnsByEq, setReturnsByEq] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/me/projects-to-dismantle").then((r) => setProjects(r.data?.projects || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get("/inventory/meta").then((r) => setMeta(r.data || {})).catch(() => {});
  }, []);

  const open = (p) => {
    setPicked(p);
    // Pre-populate return locations with originals (no photos yet)
    const initial = {};
    (p.finalization?.equipment_left || []).forEach((eq) => {
      if (!eq.picked_up) {
        initial[eq.id] = {
          photo: null,
          return_location: eq.original_location || "",
          return_color: eq.original_color || "",
          return_shelf_row: eq.original_shelf_row || "",
          return_shelf_col: eq.original_shelf_col || "",
        };
      }
    });
    setReturnsByEq(initial);
  };

  const updateReturn = (eqId, patch) => {
    setReturnsByEq((s) => ({ ...s, [eqId]: { ...(s[eqId] || {}), ...patch } }));
  };

  const isLocationChanged = (eq, ret) => {
    if (!ret) return false;
    return (
      (ret.return_location || "") !== (eq.original_location || "")
      || (ret.return_color || "") !== (eq.original_color || "")
      || (ret.return_shelf_row || "") !== (eq.original_shelf_row || "")
      || (ret.return_shelf_col || "") !== (eq.original_shelf_col || "")
    );
  };

  const confirm = async () => {
    if (!picked) return;
    const eqs = (picked.finalization?.equipment_left || []).filter((e) => !e.picked_up);
    // Validările (poză + locație de returnare obligatorie per echipament) au fost eliminate
    // la cererea userului. Items fără poză/locație sunt acum trimise cu valori null și pot fi
    // confirmate chiar și când lista e goală (cazul 0/0).
    const items = eqs
      .map((eq) => {
        const r = returnsByEq[eq.id] || {};
        return {
          equipment_id: eq.id,
          return_photo_file_id: r.photo?.id || null,
          return_location: r.return_location || null,
          return_color: r.return_color || null,
          return_shelf_row: r.return_shelf_row || null,
          return_shelf_col: r.return_shelf_col || null,
        };
      });
    setSubmitting(true);
    try {
      await api.post(`/me/projects/${picked.id}/pickup-equipment`, { items });
      toast.success(items.length === 0 ? "Demontaj confirmat (fără echipamente de ridicat)." : "Demontaj confirmat. Echipamentele au fost reîntoarse în magazie.");
      setPicked(null);
      setReturnsByEq({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setSubmitting(false); }
  };

  if (picked) {
    const eqs = picked.finalization?.equipment_left || [];
    const photos = picked.finalization?.photo_file_ids || [];
    const remainingCount = eqs.filter((e) => !e.picked_up).length;
    const validCount = eqs.filter((e) => !e.picked_up && returnsByEq[e.id]?.photo?.id && returnsByEq[e.id]?.return_location).length;
    return (
      <ModalShell title={`🔧 Demontaj: ${picked.name}`} onClose={() => setPicked(null)} maxW="max-w-3xl">
        <div className="space-y-3">
          <div className="text-xs text-zinc-500">
            📍 {picked.adresa || "—"} · Finalizat de <strong>{picked.finalization?.finalized_by_name || "—"}</strong> la {picked.finalization?.finalized_at ? fmtRoDateTime(picked.finalization.finalized_at) : "—"}
          </div>
          {photos.length > 0 && (
            <div>
              <Label className="small-label">📷 Poze de la finalizare</Label>
              <div className="grid grid-cols-4 gap-2 mt-1">
                {photos.map((fid) => (
                  <AuthImg key={fid} fileId={fid} className="w-full h-20 object-cover rounded border"/>
                ))}
              </div>
            </div>
          )}
          {picked.finalization?.note && (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
              <strong>Notă:</strong> {picked.finalization.note}
            </div>
          )}
          <div>
            <Label className="small-label">📦 Echipamente de ridicat — pentru fiecare: poză + locație de returnare</Label>
            <div className="mt-2 space-y-2">
              {eqs.length === 0 ? (
                <div className="p-3 text-xs text-zinc-400">Niciun echipament listat.</div>
              ) : eqs.map((eq) => {
                const already = !!eq.picked_up;
                const r = returnsByEq[eq.id] || {};
                const changed = isLocationChanged(eq, r);
                const hasPhoto = !!r.photo?.id;
                return (
                  <div
                    key={eq.id}
                    className={`border rounded p-3 ${already ? "bg-emerald-50 border-emerald-200" : hasPhoto ? "bg-emerald-50/40 border-emerald-300" : "bg-white border-zinc-200"}`}
                    data-testid={`dismantle-eq-${eq.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className={`w-5 h-5 rounded border flex-shrink-0 grid place-items-center text-[12px] mt-0.5 ${(hasPhoto || already) ? "bg-emerald-600 border-emerald-600 text-white" : "border-zinc-300"}`}>
                        {(hasPhoto || already) ? "✓" : ""}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">{eq.name}</div>
                        <div className="text-[11px] text-zinc-600 mt-0.5">
                          📍 <strong>Pune înapoi în:</strong> {eq.original_location || "—"}
                          {eq.original_color ? ` · culoare ${eq.original_color}` : ""}
                          {eq.original_shelf_row && eq.original_shelf_col ? ` · raft ${eq.original_shelf_row}${eq.original_shelf_col}` : ""}
                        </div>
                        {already && <div className="text-[11px] text-emerald-700 mt-0.5">✓ Deja ridicat de {eq.picked_up_by_name} · {eq.picked_up_at ? fmtRoDateTime(eq.picked_up_at) : ""}</div>}
                      </div>
                    </div>
                    {!already && (
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-[10px] uppercase text-zinc-500">Locație de returnare</Label>
                          <Select value={r.return_location || ""} onValueChange={(v) => updateReturn(eq.id, { return_location: v })}>
                            <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Alege locația"/></SelectTrigger>
                            <SelectContent>
                              {(meta.locations || []).map((loc) => (
                                <SelectItem key={loc} value={loc}>{loc}{loc === eq.original_location ? " (originală)" : ""}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {(meta.shelf_locations || []).includes(r.return_location) && (
                            <div className="grid grid-cols-2 gap-1">
                              <Select value={r.return_shelf_row || ""} onValueChange={(v) => updateReturn(eq.id, { return_shelf_row: v })}>
                                <SelectTrigger className="h-8 text-[11px]"><SelectValue placeholder="Raft (A-J)"/></SelectTrigger>
                                <SelectContent>
                                  {(meta.rows || []).map((row) => (<SelectItem key={row} value={row}>{row}</SelectItem>))}
                                </SelectContent>
                              </Select>
                              <Input
                                type="number" min="1" max={meta.max_col || 30}
                                placeholder="Col"
                                className="h-8 text-[11px]"
                                value={r.return_shelf_col || ""}
                                onChange={(e) => updateReturn(eq.id, { return_shelf_col: e.target.value })}
                              />
                            </div>
                          )}
                          {changed && (
                            <div className="text-[10px] text-amber-700 italic">⚠ Locație diferită de cea originală — poza este justificare obligatorie.</div>
                          )}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] uppercase text-zinc-500">📷 Poză cu echipamentul așezat (obligatorie)</Label>
                          {r.photo?.id ? (
                            <div className="relative inline-block">
                              <AuthImg fileId={r.photo.id} className="h-20 w-auto rounded border border-emerald-300"/>
                              <button type="button" onClick={() => updateReturn(eq.id, { photo: null })} className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 border border-zinc-200 text-red-600">
                                <Trash2 size={10}/>
                              </button>
                            </div>
                          ) : (
                            <UploadButton
                              accept="image/*"
                              label="📷 Încarcă poză"
                              testId={`dismantle-photo-${eq.id}`}
                              onUploaded={(f) => updateReturn(eq.id, { photo: f })}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="text-[11px] text-zinc-500 mt-2">
              ⚠ Fiecare echipament necesită o poză a sa așezat în magazie. Poți schimba locația — schimbarea va fi salvată automat în istoric.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPicked(null)}>Înapoi</Button>
          <Button
            onClick={confirm}
            disabled={submitting}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="dismantle-confirm"
          >
            {submitting ? "Se confirmă…" : (remainingCount === 0 ? "Confirmă demontaj" : `Confirmă (${validCount}/${remainingCount})`)}
          </Button>
        </DialogFooter>
      </ModalShell>
    );
  }

  return (
    <ModalShell title="🔧 Șantiere de demontat" onClose={onClose} maxW="max-w-xl">
      <div className="space-y-2">
        {projects.length === 0 ? (
          <div className="text-sm text-zinc-500 italic text-center py-6">Niciun șantier de demontat alocat ție pentru azi.</div>
        ) : projects.map((p) => {
          const eqs = p.finalization?.equipment_left || [];
          const remaining = eqs.filter((e) => !e.picked_up).length;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => open(p)}
              className="w-full text-left card-flat p-3 hover:border-orange-400"
              data-testid={`dismantle-project-${p.id}`}
            >
              <div className="font-display font-bold">{p.name}</div>
              <div className="text-xs text-zinc-500">📍 {p.adresa || "—"}</div>
              <div className="text-xs text-orange-700 mt-1">
                📦 {remaining} {remaining === 1 ? "echipament" : "echipamente"} de ridicat
              </div>
            </button>
          );
        })}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Închide</Button>
      </DialogFooter>
    </ModalShell>
  );
}

// ---------- My Equipment (self-view for employee) ----------
function MyEquipmentModal({ onClose }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [issuances, setIssuances] = useState([]);
  const [issuancesLoaded, setIssuancesLoaded] = useState(false);

  useEffect(() => {
    if (!user?.person_id) {
      setData({ items: [], counts: { total: 0, echipament: 0, psa: 0 }, no_person: true });
      return;
    }
    api.get(`/persons/${user.person_id}/assigned-items`)
      .then((r) => setData(r.data))
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Eroare la încărcare");
        setData({ items: [], counts: { total: 0, echipament: 0, psa: 0 } });
      });
  }, [user?.person_id]);

  useEffect(() => {
    api.get("/inventory/issuances/me")
      .then((r) => setIssuances(r.data || []))
      .catch(() => setIssuances([]))
      .finally(() => setIssuancesLoaded(true));
  }, []);

  const counts = data?.counts || { total: 0, echipament: 0, psa: 0 };
  const items = data?.items || [];

  return (
    <ModalShell title="Echipament alocat" onClose={onClose} maxW="max-w-2xl" swissBlack>
      <div data-testid="my-equipment-dialog" className="space-y-4 mt-2">
        {!data ? (
          <div className="p-6 text-sm text-zinc-400 text-center">Se încarcă echipamentele…</div>
        ) : data.no_person ? (
          <div className="p-6 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded text-center">
            Contul tău nu este încă asociat cu o fișă de angajat. Contactează administratorul.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="card-flat p-3 text-center">
                <div className="small-label">Total alocat</div>
                <div className="font-display text-2xl font-extrabold text-zinc-800">{counts.total}</div>
              </div>
              <div className="card-flat p-3 text-center">
                <div className="small-label">Echipamente</div>
                <div className="font-display text-2xl font-extrabold text-zinc-700">{counts.echipament}</div>
              </div>
              <div className="card-flat p-3 text-center bg-cyan-50 border-cyan-200">
                <div className="small-label text-cyan-800">PSA</div>
                <div className="font-display text-2xl font-extrabold text-cyan-800">{counts.psa}</div>
              </div>
              <div className="card-flat p-3 text-center bg-amber-50 border-amber-200">
                <div className="small-label text-amber-800">Preluări</div>
                <div className="font-display text-2xl font-extrabold text-amber-800">{issuances.length}</div>
              </div>
            </div>

            {/* Section 1: Allocated by company (admin/planificator) */}
            <div>
              <div className="flex items-center gap-2 mt-2 mb-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-700">
                  Alocat de companie
                </span>
                <span className="text-[11px] text-zinc-400">({items.length})</span>
              </div>
              {items.length === 0 ? (
                <div className="p-5 text-center text-zinc-400 italic text-sm border border-dashed rounded-md" data-testid="my-equipment-empty">
                  Nimic alocat oficial.
                </div>
              ) : (
                <div className="space-y-2 max-h-[35vh] overflow-y-auto" data-testid="my-equipment-list">
                  {items.map((it) => (
                    <div key={it.item_id} className="card-flat p-3 flex items-start gap-3" data-testid={`my-equipment-item-${it.item_id}`}>
                      <div className="w-14 h-14 flex-shrink-0 rounded-md overflow-hidden bg-zinc-100 flex items-center justify-center">
                        {it.photo_id ? (
                          <AuthImg fileId={it.photo_id} className="w-full h-full object-cover"/>
                        ) : (
                          <Package size={22} className="text-zinc-400"/>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {it.item_type === "psa" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800 font-bold">PSA</span>}
                          <span className="font-semibold truncate">{it.item_name || "—"}</span>
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-1 space-y-0.5">
                          <div>📅 Predat pe <b>{String(it.handover_ts || "").slice(0, 10) || "—"}</b>{it.handover_by ? <> · de <b>{it.handover_by}</b></> : null}</div>
                          <div>📍 Locație curentă: <b>{it.location || "—"}</b></div>
                          {it.note && <div className="italic">📝 {it.note}</div>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Section 2: Self-issued from inventory */}
            <div>
              <div className="flex items-center gap-2 mt-2 mb-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-700">
                  Preluate de mine din inventar
                </span>
                <span className="text-[11px] text-zinc-400">({issuances.length})</span>
              </div>
              {!issuancesLoaded ? (
                <div className="p-5 text-center text-sm text-zinc-400">Se încarcă…</div>
              ) : issuances.length === 0 ? (
                <div className="p-5 text-center text-zinc-400 italic text-sm border border-dashed rounded-md" data-testid="my-issuances-empty">
                  Nu ai preluat nimic din inventar.
                </div>
              ) : (
                <div className="space-y-2 max-h-[35vh] overflow-y-auto" data-testid="my-issuances-list">
                  {issuances.map((iss) => {
                    const typeLabels = { echipament: "Echipament", psa: "PSA", consumabil: "Consumabil", piesa_auto: "Piesă auto" };
                    const isPsa = iss.item_type === "psa";
                    const isEch = iss.item_type === "echipament";
                    return (
                      <div key={iss.id} className="card-flat p-3 flex items-start gap-3" data-testid={`my-issuance-${iss.id}`}>
                        <div className="w-14 h-14 flex-shrink-0 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-center">
                          <Package size={22} className="text-amber-700"/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {isPsa && <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800 font-bold">PSA</span>}
                            {isEch && <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-700 font-bold">ECHIP.</span>}
                            {iss.item_type && !isPsa && !isEch && <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 font-bold">{(typeLabels[iss.item_type] || iss.item_type).toUpperCase()}</span>}
                            <span className="font-semibold truncate">{iss.item_name || "—"}</span>
                            <span className="ml-auto text-xs font-bold text-amber-800 tabular-nums">
                              {Number(iss.quantity || 0).toLocaleString("ro-RO")} {iss.item_unit || ""}
                            </span>
                          </div>
                          <div className="text-[11px] text-zinc-500 mt-1 space-y-0.5">
                            <div>📅 Preluat pe <b>{String(iss.ts || "").slice(0, 10) || "—"}</b></div>
                            {iss.project_name && <div>🏗️ Proiect: <b>{iss.project_name}</b></div>}
                            {iss.note && <div className="italic">📝 {iss.note}</div>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        <DialogFooter className="mt-6">
          <button type="button" onClick={onClose} className="sb-outline-btn" data-testid="my-equipment-close">Închide</button>
        </DialogFooter>
      </div>
    </ModalShell>
  );
}


function ChangePasswordModal({ onClose }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!currentPassword) {
      toast.error("Introdu parola curentă");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Parola nouă trebuie să aibă minim 8 caractere");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Parolele noi nu coincid");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/me", { current_password: currentPassword, new_password: newPassword });
      toast.success("Parolă schimbată cu succes");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la schimbarea parolei");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="🔑 Schimbă parola" onClose={onClose} maxW="max-w-md">
      <div className="space-y-3 py-2" data-testid="change-password-modal">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700 mb-1 block">Parola curentă</Label>
          <div className="relative">
            <Input
              type={showCurrent ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="•••••••"
              data-testid="cp-current"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowCurrent(!showCurrent)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-zinc-500 hover:text-zinc-900"
            >
              {showCurrent ? "🙈" : "👁"}
            </button>
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700 mb-1 block">Parolă nouă</Label>
          <div className="relative">
            <Input
              type={showNew ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="minim 8 caractere"
              data-testid="cp-new"
            />
            <button
              type="button"
              onClick={() => setShowNew(!showNew)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-zinc-500 hover:text-zinc-900"
            >
              {showNew ? "🙈" : "👁"}
            </button>
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700 mb-1 block">Confirmă parola nouă</Label>
          <Input
            type={showNew ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="rescrie parola nouă"
            data-testid="cp-confirm"
          />
        </div>
        <div className="text-[10px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded p-2">
          Parola trebuie să aibă minim 8 caractere, o literă mare, o literă mică și o cifră.
        </div>
      </div>
      <DialogFooter className="mt-4 gap-2">
        <button type="button" onClick={onClose} className="sb-outline-btn" disabled={saving}>Anulează</button>
        <Button
          onClick={submit}
          disabled={saving}
          className="bg-amber-600 hover:bg-amber-700"
          data-testid="cp-submit"
        >
          {saving ? "Se salvează..." : "🔑 Schimbă parola"}
        </Button>
      </DialogFooter>
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ReturnInventoryModal — self-service pentru returnarea echipamentului propriu
// ─────────────────────────────────────────────────────────────────────────────
function ReturnInventoryModal({ onClose }) {
  const [issuances, setIssuances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [returning, setReturning] = useState({}); // {id: bool}
  const [confirmId, setConfirmId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/inventory/issuances/me")
      .then((r) => setIssuances(Array.isArray(r.data) ? r.data : []))
      .catch(() => setIssuances([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const doReturn = async (iss) => {
    setReturning((m) => ({ ...m, [iss.id]: true }));
    try {
      await api.post(`/inventory/issuances/${iss.id}/return-by-me`);
      toast.success(`✅ "${iss.item_name}" returnat în inventar`);
      setIssuances((arr) => arr.filter((x) => x.id !== iss.id));
      setConfirmId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la returnare");
    } finally {
      setReturning((m) => ({ ...m, [iss.id]: false }));
    }
  };

  return (
    <ModalShell title="↩️ Returnează în inventar" onClose={onClose} maxW="max-w-2xl" swissBlack>
      <div data-testid="return-inventory-dialog" className="space-y-3 mt-2">
        {loading ? (
          <div className="p-8 text-sm text-zinc-400 text-center">Se încarcă echipamentele tale…</div>
        ) : issuances.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-5xl mb-2">📦</div>
            <div className="text-sm text-zinc-300 font-semibold">Nu ai niciun echipament de returnat.</div>
            <div className="text-xs text-zinc-500 mt-1">Lista ta de preluări e goală.</div>
          </div>
        ) : (
          <>
            <div className="text-[11px] uppercase tracking-wider text-zinc-400 px-1">
              {issuances.length} {issuances.length === 1 ? "obiect alocat" : "obiecte alocate"} — click pe butonul din dreapta pentru a-l returna
            </div>
            <div className="space-y-2">
              {issuances.map((iss) => {
                const isReturning = !!returning[iss.id];
                const isConfirming = confirmId === iss.id;
                return (
                  <div
                    key={iss.id}
                    className={`flex items-center gap-3 border rounded-lg p-3 transition-colors ${
                      isConfirming ? "border-amber-400 bg-amber-500/10" : "border-white/15 bg-white/5 hover:bg-white/10"
                    }`}
                    data-testid={`return-row-${iss.id}`}
                  >
                    {/* Thumbnail */}
                    {iss.photo_id ? (
                      <img
                        src={fileUrl(iss.photo_id)}
                        alt={iss.item_name || "echipament"}
                        className="w-12 h-12 rounded-md object-cover border border-white/20 flex-shrink-0 bg-zinc-800"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-md border border-dashed border-white/20 flex items-center justify-center text-white/40 bg-zinc-900 flex-shrink-0">
                        <ImageOff size={16} />
                      </div>
                    )}
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-white text-sm truncate" title={iss.item_name}>
                        {iss.item_name || "—"}
                      </div>
                      <div className="text-[11px] text-zinc-400 flex items-center gap-2 flex-wrap mt-0.5">
                        {iss.item_serial && (
                          <span className="font-mono bg-white/10 px-1.5 py-0.5 rounded">
                            S/N: {iss.item_serial}
                          </span>
                        )}
                        <span className="font-mono">
                          {Number(iss.quantity || 0).toLocaleString("ro-RO")} {iss.item_unit || "buc"}
                        </span>
                        {iss.ts && (
                          <span className="text-zinc-500">
                            preluat: {fmtRoDate(iss.ts.slice(0, 10))}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Right-side action */}
                    {!isConfirming ? (
                      <button
                        type="button"
                        onClick={() => setConfirmId(iss.id)}
                        disabled={isReturning}
                        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-md bg-cyan-600 hover:bg-cyan-700 active:bg-cyan-800 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider transition-colors shadow-md shadow-cyan-600/30"
                        data-testid={`return-btn-${iss.id}`}
                        title="Returnează în inventar"
                      >
                        <Undo2 size={14} />
                        <span className="hidden sm:inline">Returnează</span>
                      </button>
                    ) : (
                      <div className="flex-shrink-0 flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setConfirmId(null)}
                          disabled={isReturning}
                          className="px-2.5 py-2 rounded-md border border-white/30 text-white/80 hover:bg-white/10 text-xs font-bold uppercase tracking-wider"
                          data-testid={`return-cancel-${iss.id}`}
                        >
                          Nu
                        </button>
                        <button
                          type="button"
                          onClick={() => doReturn(iss)}
                          disabled={isReturning}
                          className="px-3 py-2 rounded-md bg-amber-500 hover:bg-amber-600 active:bg-amber-700 disabled:opacity-50 text-zinc-900 text-xs font-extrabold uppercase tracking-wider shadow-md"
                          data-testid={`return-confirm-${iss.id}`}
                        >
                          {isReturning ? "Se trimite…" : "Da, returnează"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-zinc-500 text-center pt-2">
              💡 Cantitatea returnată se adaugă automat în stocul magaziei.
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
}
